// PharmaPOS Mall — public storefront SPA (mall.pharmapos.com)
//
// Shopee/Lazada-style single-page app: browse published items from every
// connected outlet, cart/checkout for pickup (pay online via toyyibPay or at
// the counter), and an in-store scan & go mode (scan the outlet QR, scan
// shelf items, send the cart to the counter to pay).
//
// Cart model v1: ONE outlet per cart — multi-outlet settlement is out of
// scope (each outlet receives its own payment). Adding an item from another
// outlet prompts to start a new cart.

import { LitElement, html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { api, MallItem, MallOrder, MallOutlet, MallCategory, ApiError } from './lib/api.js';
import {
  categoryIcon, rootCategories, childrenOf, findCategoryBySlug, findCategoryById,
  categoryPath, categoryHref,
} from './lib/categories.js';
import { addToCart, cartCount, loadCart, MallCart, removeItem, saveCart, updateQuantity } from './lib/cart.js';
import { getIdToken, onAuthChange, signInWithGoogle, signOut, completeEmailLinkSignIn, isSignInWithEmailLink, sendSignInLink } from './lib/firebase.js';
import {
  SavedOutlet, RecentOutlet,
  loadFavouriteOutlets, loadRecentOutlets, isFavouriteOutlet, toggleFavouriteOutlet,
  recordOutletView, clearRecentOutlets, refreshSavedOutletNames, SAVED_OUTLETS_CHANGED_EVENT,
} from './lib/outlets.js';
import { APP_DISPLAY_NAME, DEMO_SUFFIX } from './lib/demo.js';
import { enablePush, disablePush, onPushMessage, pushConfigured, pushPermission } from './lib/push.js';
import { siteBrand, isRetailProfile } from './lib/site-config.js';
import { consumeHandoffFragment, handoffSession, setHandoffSession } from './lib/sso.js';

/** Retail-stall deployment — shop wording and icons instead of pharmacy ones. */
const RETAIL = isRetailProfile();
/** Placeholder shown when an item has no image. */
const ITEM_PLACEHOLDER = RETAIL ? '🛒' : '💊';

declare global {
  interface Window {
    BarcodeDetector?: any;
  }
}

interface ScanLine { stockId: string; code: string; name: string; price: number; quantity: number; }

/** Minimal outlet shape shared by search results, saved-list rows and the
 *  store page header (saved entries may not be in the live outlet list). */
interface OutletRowData {
  code: string;
  name: string;
  location?: string;
  online?: boolean;
  itemCount?: number;
  distanceKm?: number;
}

/** Shopper-facing provider name. */
function paymentProviderLabel(provider: string): string {
  if (provider === 'billplz') return 'Billplz';
  if (provider === 'toyyibpay') return 'ToyyibPay';
  return 'the payment gateway';
}

// The provider redirect only carries the order id back. The hint remembers who
// the shopper was sent to, so the waiting screen can name the provider before
// the first status poll returns.
const PAY_RETURN_HINT_KEY = 'ppos_mall_pending_payment';
const PAY_RETURN_POLL_MS = 2000;
const PAY_RETURN_MAX_WAIT_MS = 90_000;

interface PendingPaymentHint {
  orderId: string;
  provider?: string;
  refNo?: string;
  at: number;
}

function savePendingPayment(hint: PendingPaymentHint): void {
  try {
    sessionStorage.setItem(PAY_RETURN_HINT_KEY, JSON.stringify(hint));
  } catch {
    /* private mode — the return page still works from the URL alone */
  }
}

function loadPendingPayment(): PendingPaymentHint | null {
  try {
    const raw = sessionStorage.getItem(PAY_RETURN_HINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPaymentHint;
    return parsed && typeof parsed.orderId === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** Drop the hint once it is settled, or when it belongs to another order. */
function clearPendingPayment(orderId?: string): void {
  try {
    const hint = loadPendingPayment();
    if (orderId && hint && hint.orderId !== orderId) return;
    sessionStorage.removeItem(PAY_RETURN_HINT_KEY);
  } catch {
    /* ignore */
  }
}

@customElement('app-root')
export class AppRoot extends LitElement {
  // ── Routing ──
  @state() private _path = window.location.pathname;
  @state() private _query = new URLSearchParams(window.location.search);

  // ── Auth ──
  @state() private _email: string | null = null;
  @state() private _authReady = false;
  @state() private _loginOpen = false;
  @state() private _loginReturn = '/';
  @state() private _loginEmail = '';
  @state() private _loginLinkSent = false;
  @state() private _loginBusy = false;
  @state() private _loginError = '';
  /** Email we were signed in with by the POS app (see lib/sso.ts). */
  private _ssoEmail: string | null = null;
  /** Email of a real Firebase session on THIS origin, if any. */
  private _firebaseEmail: string | null = null;

  // ── Cart ──
  @state() private _cart: MallCart | null = loadCart();

  // ── Saved outlets (device-local: favourites + recently viewed) ──
  @state() private _favOutlets: SavedOutlet[] = loadFavouriteOutlets();
  @state() private _recentOutlets: RecentOutlet[] = loadRecentOutlets();

  // ── Home ──
  @state() private _outlets: MallOutlet[] = [];
  @state() private _homeItems: MallItem[] = [];
  @state() private _homeOutletQuery = '';
  @state() private _homeTotal = 0;
  @state() private _homePage = 1;
  @state() private _homeLoading = false;
  @state() private _homeError = '';

  // ── Categories (gateway-owned master taxonomy) ──
  @state() private _categories: MallCategory[] = [];
  @state() private _categoriesLoading = false;
  @state() private _categoriesError = '';

  // ── Category page (/c/<slug>) ──
  @state() private _catSlug = '';
  @state() private _catItems: MallItem[] = [];
  @state() private _catTotal = 0;
  @state() private _catPage = 1;
  @state() private _catLoading = false;
  @state() private _catError = '';
  @state() private _catStore = '';

  // ── Search ──
  @state() private _queryText = '';
  @state() private _searchInput = '';
  @state() private _searchItems: MallItem[] = [];
  @state() private _searchTotal = 0;
  @state() private _searchPage = 1;
  @state() private _searchLoading = false;

  // ── Outlet discovery (/outlets) ──
  @state() private _outletQuery = '';
  @state() private _outletNear = false;
  @state() private _outletRadius = 25;
  @state() private _outletResults: MallOutlet[] = [];
  @state() private _outletTotal = 0;
  @state() private _outletPage = 1;
  @state() private _outletLoading = false;
  @state() private _outletError = '';
  @state() private _outletCoords: { lat: number; lng: number } | null = null;
  @state() private _outletGeoError = '';
  private _outletSearchTimer?: number;
  private _outletSearchSeq = 0;

  /** Outlet results are only shown after a name/area search or "near me" —
   *  never as a complete list (a chain can have 100+ outlets). */
  private get _outletSearchActive(): boolean {
    return !!this._outletQuery.trim() || (this._outletNear && !!this._outletCoords);
  }

  // ── Store page ──
  @state() private _storeItems: MallItem[] = [];
  @state() private _storeLoading = false;

  // ── Product page ──
  @state() private _product: MallItem | null = null;
  @state() private _productQty = 1;
  @state() private _productLoading = false;

  // ── Checkout ──
  // Shopper identity: members become a "Hold Sales" at the counter,
  // wholesale customers a "Hold Wholesales". The profile endpoint tells us
  // who owns the signed-in email; unknown emails get the sign-up form.
  @state() private _profile: { found: boolean; shopperType: string; name: string; companyName: string; address: string; phone: string } | null = null;
  @state() private _profileLoading = false;
  @state() private _shopperType: 'member' | 'customer' = 'member';
  @state() private _checkoutName = '';
  @state() private _checkoutPhone = '';
  @state() private _checkoutCompany = '';
  @state() private _checkoutAddress = '';
  @state() private _checkoutPayment: 'counter' | 'online' = 'counter';
  // Online (toyyibPay) is only offered once the outlet has configured its
  // merchant account; stays false until the payment-options call succeeds.
  @state() private _onlinePayment = false;
  @state() private _onlinePaymentNote = '';
  @state() private _paymentProviderLabel = '';
  @state() private _paymentFeeMode = 'outlet';
  @state() private _paymentFee = 0;
  @state() private _paymentCustomerTotal = 0;
  private _paymentOptionsKey = '';
  @state() private _checkoutBusy = false;
  /** Order id whose confirmation is in flight (offline order → confirm). */
  @state() private _confirmBusy = '';
  /** Notification permission for the “tell me when my order is ready” banner. */
  @state() private _pushPermission: NotificationPermission | 'unsupported' = pushPermission();
  @state() private _pushBusy = false;
  @state() private _checkoutError = '';
  private _profileKey = '';

  // ── Orders ──
  @state() private _orders: MallOrder[] = [];
  @state() private _ordersLoading = false;
  @state() private _ordersError = '';
  @state() private _expandedOrder = '';

  // ── Payment return (/payment/return?order=…) ──
  // Driven ONLY by asking the gateway about the order in the URL. Provider
  // callbacks are never used here: an unverifiable one identifies nothing we
  // can trust, and it may belong to a different order entirely.
  /** verifying | paid | unconfirmed | cancelled | signin | error */
  @state() private _payReturnStatus = 'verifying';
  @state() private _payReturnProvider = '';
  @state() private _payReturnOrderId = '';
  @state() private _payReturnRefNo = '';
  @state() private _payReturnDetail = '';
  @state() private _payReturnCancelable = true;
  @state() private _payReturnCancelBusy = false;
  private _payReturnTimer?: number;
  /** Bumped on every start/cancel so stale polls cannot overwrite the UI. */
  private _payReturnSeq = 0;
  /** Bounded wait for Firebase to restore the session after the redirect. */
  private _payReturnAuthWaits = 0;

  // ── In-store scan & go ──
  @state() private _scanLines: ScanLine[] = [];
  @state() private _scanResult: MallItem | null = null;
  @state() private _scanMessage = '';
  @state() private _scanCameraOn = false;
  @state() private _scanSupported = true;
  @state() private _scanManual = '';
  @state() private _holdInfo: { holdId: string; total: number; message: string } | null = null;

  @state() private _toast = '';

  @query('#scan-video') private _videoEl?: HTMLVideoElement;
  private _scanStream: MediaStream | null = null;
  private _scanTimer?: number;
  private _scanFallbackTimer?: number;
  private _scanControls?: { stop: () => void };
  private _scanStartId = 0;
  private _scanFoundAnything = false;
  private _scanCooldown = 0;
  private _outletsTimer?: number;
  private _outletsRefreshBusy = false;
  private _pushUnsub: () => void = () => undefined;
  /** Per-outlet "itemCount:online" signature from the last consumed poll —
   *  the mall's change detector for live catalogue refreshes. */
  private _outletSig = new Map<string, string>();
  /** Catalogue changed while the shopper was on another page — reload Home
   *  the next time it is visited. */
  private _homeDirty = false;

  static styles = css`
    /* Shadow DOM does NOT inherit the document's universal box-sizing rule
       (index.html). Without this, main (width:100% + 0.75rem padding) is 24px
       wider than the viewport; mobile Chrome then shrink-to-fits the page, so the
       layout viewport grows taller than the visible screen and the fixed bottom
       nav (bottom:0) is pushed below the visible area — the bar disappears on
       phones. Same protection for .modal / .btn.wide / form inputs. */
    *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; height: 100%; }
    .shell { display: flex; flex-direction: column; height: 100%; min-height: 100dvh; }
    header { position: sticky; top: 0; z-index: 10; background: linear-gradient(135deg, var(--color-primary), #0d47a1); color: #fff; padding: max(0.6rem, env(safe-area-inset-top)) 0.75rem 0.6rem; display: flex; gap: 0.5rem; align-items: center; box-shadow: 0 2px 10px rgba(13,71,161,0.3); }
    header .logo { font-weight: 800; font-size: 1rem; white-space: nowrap; cursor: pointer; }
    header .search { flex: 1; display: flex; background: #fff; border-radius: 999px; overflow: hidden; min-height: 44px; }
    header .search input { flex: 1; border: 0; outline: 0; padding: 0.45rem 0.9rem; font-size: 16px; min-width: 0; }
    header .search button { border: 0; background: #fff; padding: 0 0.9rem; cursor: pointer; }
    header .account { border: 0; background: rgba(255,255,255,0.18); color: #fff; border-radius: 999px; min-height: 44px; min-width: 44px; padding: 0.4rem 0.9rem; cursor: pointer; font-size: 0.85rem; white-space: nowrap; }
    main { flex: 1; min-height: 0; overflow-y: auto; padding: 0.75rem; padding-bottom: calc(4.5rem + env(safe-area-inset-bottom)); width: 100%; max-width: 1200px; margin: 0 auto; }
    nav.bottom { position: fixed; bottom: 0; left: 0; right: 0; z-index: 10; display: flex; background: var(--color-surface); border-top: 1px solid var(--color-border); padding-bottom: env(safe-area-inset-bottom); }
    nav.bottom button { flex: 1; min-height: 56px; border: 0; background: none; padding: 0.5rem 0.25rem 0.4rem; font-size: 0.72rem; color: var(--color-text-secondary); cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 0.15rem; }
    nav.bottom button.active { color: var(--color-primary); font-weight: 600; }
    nav.bottom .icon { font-size: 1.15rem; }
    .badge { position: relative; }
    .badge[data-count]:after { content: attr(data-count); position: absolute; top: -4px; right: -8px; background: var(--color-error); color: #fff; border-radius: 999px; font-size: 0.62rem; padding: 0 4px; line-height: 1.3; }
    .chips { display: flex; gap: 0.5rem; overflow-x: auto; padding-bottom: 0.6rem; scroll-snap-type: x proximity; }
    .chips button { flex: 0 0 auto; scroll-snap-align: start; min-height: 40px; border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 999px; padding: 0.3rem 0.9rem; font-size: 0.85rem; white-space: nowrap; cursor: pointer; }
    .chips button.selected { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }
    .chips button.offline { opacity: 0.55; }
    .offline-banner { background: #fff8e1; border: 1px solid #ffe082; color: #8d6e63; border-radius: var(--radius); padding: 0.6rem 0.75rem; font-size: 0.83rem; margin: 0.5rem 0; }
    .confirm-panel { margin-top: 0.45rem; padding: 0.55rem 0.65rem; border: 1px dashed var(--color-primary); border-radius: var(--radius); background: #f7fbf7; }
    .confirm-actions { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-top: 0.4rem; }
    .outlet-finder { background: var(--color-surface); border-radius: 14px; padding: 0.7rem; margin-bottom: 0.75rem; box-shadow: 0 1px 3px rgba(32,33,36,0.1); }
    .finder-title { font-weight: 600; font-size: 0.9rem; margin-bottom: 0.5rem; }
    .finder-sep { text-align: center; color: var(--color-text-secondary); font-size: 0.76rem; margin: 0.55rem 0 0.35rem; }
    .finder-row { display: flex; gap: 0.4rem; align-items: stretch; }
    .finder-row input { flex: 1; min-width: 0; min-height: 48px; padding: 0.5rem 0.8rem; font-size: 16px; border: 1px solid var(--color-border); border-radius: 10px; }
    .finder-row .btn { min-width: 52px; }
    .finder-actions { display: flex; gap: 0.4rem; margin-top: 0.5rem; flex-wrap: wrap; align-items: center; }
    .finder-actions .btn { min-height: 40px; padding: 0.35rem 0.85rem; font-size: 0.85rem; }
    .finder-actions select.radius { min-height: 40px; border: 1px solid var(--color-border); border-radius: 10px; background: var(--color-surface); padding: 0 0.5rem; font-size: 0.85rem; }
    .outlet-list { display: flex; flex-direction: column; gap: 0.5rem; }
    .outlet-row { display: flex; align-items: center; gap: 0.6rem; width: 100%; text-align: left; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px; padding: 0.65rem 0.75rem; cursor: pointer; color: var(--color-text); font: inherit; }
    .outlet-row.compact { padding: 0.5rem 0.6rem; }
    .outlet-row .pin { font-size: 1.15rem; }
    .outlet-row .grow { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.1rem; }
    .outlet-row .title { font-weight: 600; font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .outlet-row .muted { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .outlet-row .chipsline { color: var(--color-text-secondary); font-size: 0.76rem; }
    .outlet-row.offline { opacity: 0.6; }
    .outlet-row .chev { color: var(--color-text-secondary); font-size: 1.2rem; }
    .outlet-row .fav { flex: 0 0 auto; width: 44px; height: 44px; border: 0; background: none; padding: 0; font-size: 1.4rem; line-height: 1; color: #b0b6bd; cursor: pointer; border-radius: 50%; }
    .outlet-row .fav.on { color: #ffb300; }
    .outlet-row .fav:active { background: rgba(0,0,0,0.06); }
    .saved-block { margin-bottom: 0.75rem; }
    .saved-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
    .saved-head .section-title { margin: 0; }
    .link { border: 0; background: none; color: var(--color-primary); font-size: 0.82rem; font-weight: 600; cursor: pointer; padding: 0.3rem 0.2rem; }
    .btn.star-on { background: #fff8e1; border-color: #ffd54f; color: #e65100; }
    .page-head .grow { flex: 1; min-width: 0; }
    .more-link { text-align: center; color: var(--color-primary); font-size: 0.85rem; font-weight: 600; padding: 0.5rem 0 0.1rem; cursor: pointer; }
    .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.6rem; }
    @media (min-width: 560px) { .grid { grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 0.8rem; } }
    .card { position: relative; background: var(--color-surface); border-radius: 14px; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; box-shadow: 0 1px 3px rgba(32,33,36,0.1); transition: box-shadow 0.15s, transform 0.15s; }
    .card:hover { box-shadow: 0 6px 18px rgba(32,33,36,0.16); transform: translateY(-2px); }
    .card:active { transform: scale(0.98); }
    .card .add { position: absolute; right: 0.5rem; bottom: 0.5rem; width: 40px; height: 40px; border: 0; border-radius: 50%; background: var(--color-primary); color: #fff; font-size: 1.4rem; line-height: 1; cursor: pointer; box-shadow: 0 3px 8px rgba(26,115,232,0.4); }
    .skeleton .thumb, .skeleton .line { background: linear-gradient(90deg, #eceff1 25%, #f5f6f7 50%, #eceff1 75%); background-size: 200% 100%; animation: shimmer 1.2s infinite; }
    .skeleton .line { height: 0.8rem; border-radius: 4px; margin-top: 0.4rem; }
    @keyframes shimmer { to { background-position: -200% 0; } }
    .card .thumb { aspect-ratio: 1; background: #f0f2f5; display: flex; align-items: center; justify-content: center; color: #b0b6bd; font-size: 2rem; overflow: hidden; }
    .card .thumb img { width: 100%; height: 100%; object-fit: cover; }
    .card .info { padding: 0.5rem; display: flex; flex-direction: column; gap: 0.2rem; flex: 1; }
    .card .name { font-size: 0.82rem; line-height: 1.25; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 2.05em; }
    .card .price { color: var(--color-primary); font-weight: 800; font-size: 1rem; }
    .card .store { color: var(--color-text-secondary); font-size: 0.72rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .card .oos { color: var(--color-error); font-size: 0.72rem; font-weight: 600; }
    .section-title { font-size: 0.95rem; font-weight: 700; margin: 0.5rem 0; }
    /* Category tiles are one scrollable strip, not a wrapping grid: a 5th tile
       used to drop alone onto a second row, and on narrow phones the names were
       squeezed into unreadable columns. Tiles keep a usable width and the row
       scrolls sideways only when the categories genuinely don't fit. */
    .cat-tiles { display: flex; gap: 0.5rem; overflow-x: auto; padding-bottom: 0.4rem; margin-bottom: 0.75rem; scroll-snap-type: x proximity; }
    .cat-tile { position: relative; flex: 1 0 84px; display: flex; flex-direction: column; align-items: center; gap: 0.2rem; min-height: 84px; border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 14px; padding: 0.45rem 0.25rem; cursor: pointer; scroll-snap-align: start; }
    .cat-tile:active { transform: scale(0.97); }
    /* One-line scroll rows — sub-category/outlet pills (.chips) and category
       tiles (.cat-tiles): no scrollbar on touch, a thin one on mouse/keyboard
       devices. Vertical mouse wheels are mapped to sideways scrolling by
       _onRowWheel, since browsers only do that for trackpads by default. */
    .chips, .cat-tiles { scrollbar-width: none; -webkit-overflow-scrolling: touch; }
    .chips::-webkit-scrollbar, .cat-tiles::-webkit-scrollbar { display: none; }
    @media (hover: hover) and (pointer: fine) {
      .chips, .cat-tiles { scrollbar-width: thin; }
      .chips::-webkit-scrollbar, .cat-tiles::-webkit-scrollbar { display: block; height: 6px; }
      .chips::-webkit-scrollbar-thumb, .cat-tiles::-webkit-scrollbar-thumb { background: var(--color-border); border-radius: 3px; }
    }
    .cat-icon { font-size: 1.6rem; line-height: 1.1; }
    .cat-name { font-size: 0.7rem; line-height: 1.2; text-align: center; color: var(--color-text); }
    .cat-count { font-size: 0.62rem; color: var(--color-text-secondary); }
    .muted { color: var(--color-text-secondary); font-size: 0.82rem; }
    .btn { min-height: 44px; border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 10px; padding: 0.5rem 1rem; cursor: pointer; font-size: 0.95rem; }
    .btn.primary { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }
    .btn.danger { color: var(--color-error); border-color: var(--color-error); }
    .btn:disabled { opacity: 0.5; cursor: default; }
    .btn.wide { width: 100%; }
    .empty { text-align: center; color: var(--color-text-secondary); padding: 2rem 1rem; }
    .error { color: var(--color-error); margin: 0.5rem 0; }
    .page-head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
    .back { border: 0; background: none; font-size: 1.15rem; cursor: pointer; padding: 0.2rem 0.4rem; }
    .detail { background: var(--color-surface); border-radius: var(--radius); padding: 0.9rem; }
    .detail .price { font-size: 1.3rem; font-weight: 700; color: var(--color-primary); }
    .qty { display: flex; align-items: center; gap: 0.5rem; }
    .qty button { width: 2.75rem; height: 2.75rem; border-radius: 8px; border: 1px solid var(--color-border); background: var(--color-surface); font-size: 1.1rem; cursor: pointer; }
    .carousel { display: flex; gap: 0.4rem; overflow-x: auto; }
    .carousel img { width: 100%; border-radius: var(--radius); aspect-ratio: 1; object-fit: cover; }
    .cand { width: 82%; flex: 0 0 auto; }
    .list { display: flex; flex-direction: column; gap: 0.5rem; }
    .row { background: var(--color-surface); border-radius: var(--radius); padding: 0.6rem 0.75rem; display: flex; gap: 0.6rem; align-items: center; }
    .row img { width: 52px; height: 52px; border-radius: 8px; object-fit: cover; background: #f0f2f5; }
    .row .grow { flex: 1; min-width: 0; }
    .row .title { font-size: 0.86rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .status { display: inline-block; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.75rem; background: #e3f2fd; }
    .status.completed { background: #eceff1; color: #607d8b; }
    .status.cancelled { background: #ffebee; color: #c62828; }
    .status.pending { background: #fff8e1; }
    .status.awaiting_payment { background: #fff8e1; }
    .status.paid { background: #e8f5e9; }
    .status.ready { background: #ede7f6; }
    .status.recalled { background: #fff3e0; color: #e65100; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 1rem; }
    .modal { background: var(--color-surface); border-radius: 16px; max-width: 420px; width: 100%; padding: 1.1rem 1.2rem; max-height: 85dvh; overflow-y: auto; }
    @media (max-width: 560px) {
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; border-radius: 18px 18px 0 0; padding-bottom: calc(1.2rem + env(safe-area-inset-bottom)); }
    }
    .modal h3 { margin: 0 0 0.6rem; }
    .form-group { margin-bottom: 0.7rem; }
    .form-group label { display: block; font-size: 0.8rem; color: var(--color-text-secondary); margin-bottom: 0.25rem; }
    .form-group input, .form-group select { width: 100%; min-height: 48px; padding: 0.5rem 0.7rem; font-size: 16px; border: 1px solid var(--color-border); border-radius: 10px; }
    .pay-choice { display: flex; flex-direction: column; gap: 0.5rem; }
    .pay-choice label { display: flex; gap: 0.5rem; align-items: flex-start; border: 1px solid var(--color-border); border-radius: 8px; padding: 0.6rem; cursor: pointer; font-size: 0.88rem; line-height: 1.35; }
    .pay-choice input[type="radio"] {
      flex: 0 0 auto; width: 1.15rem; height: 1.15rem; min-height: 0;
      margin: 0.1rem 0 0; padding: 0; box-sizing: border-box;
      border: 0; border-radius: 0; background: transparent;
      accent-color: var(--color-primary);
    }
    .pay-choice label > span { flex: 1; min-width: 0; }
    .toast { position: fixed; bottom: calc(4.5rem + env(safe-area-inset-bottom)); left: 50%; transform: translateX(-50%); background: rgba(32,33,36,0.92); color: #fff; padding: 0.5rem 0.9rem; border-radius: 8px; font-size: 0.85rem; z-index: 120; max-width: 90%; text-align: center; }
    video.scan { width: 100%; border-radius: var(--radius); background: #000; aspect-ratio: 3/4; object-fit: cover; }
    .hold-box { background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: var(--radius); padding: 0.8rem; margin-top: 0.6rem; }
    .banner { background: linear-gradient(120deg, var(--color-primary), #4285f4 60%, #7cb3ff); color: #fff; border-radius: 16px; padding: 1.2rem 1.1rem; margin-bottom: 0.75rem; box-shadow: 0 8px 22px rgba(26,115,232,0.25); }
    .banner h2 { margin: 0 0 0.3rem; font-size: clamp(1.15rem, 4.5vw, 1.7rem); }
    .banner p { margin: 0; font-size: 0.85rem; opacity: 0.92; }
    .load-more { display: flex; justify-content: center; margin: 1rem 0; }
  `;

  // ─────────────────────────────────────────────────────────────────────
  // Lifecycle & routing
  // ─────────────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this._onPopState);
    window.addEventListener('mall-cart-changed', this._onCartChanged);
    window.addEventListener(SAVED_OUTLETS_CHANGED_EVENT, this._onSavedOutletsChanged);
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    // Poll outlet availability so stores flip online/offline in place
    // (no reload / re-login needed) when a ppServer connects or drops.
    this._outletsTimer = window.setInterval(() => void this._refreshOutlets(), 15_000);

    // Sign-in handed over from the POS app (separate origin, so Firebase's own
    // session cannot cross over). The optimistic email keeps /checkout and
    // /orders usable immediately; the key is validated in the background.
    const handoff = consumeHandoffFragment() ?? handoffSession();
    if (handoff) {
      this._ssoEmail = handoff.email || null;
      if (this._ssoEmail) this._email = this._ssoEmail;
      void this._verifyHandoffSession(handoff.sessionKey);
    }

    onAuthChange((email) => {
      // A real Firebase sign-in on this origin supersedes the handed-over
      // POS session.
      if (email) this._clearHandoffSession();
      this._firebaseEmail = email;
      this._email = email || this._ssoEmail;
      this._authReady = true;
      if (email) {
        this._warmGatewaySession();
        // Already-allowed shoppers are re-registered silently so the gateway
        // can tell them when a saved order becomes available.
        if (this._pushPermission === 'granted') void enablePush({ silent: true });
        // Auth state arrives after the first route pass — reload the shopper
        // profile for the pages that need it.
        const seg = this._path.split('/').filter(Boolean);
        if (seg[0] === 'checkout' || (seg[0] === 's' && seg[2] === 'scan')) this._routeChanged();
        // The payment provider redirects back BEFORE Firebase restores the
        // session, so the return page must resume once the user is known.
        if (seg[0] === 'payment' && seg[1] === 'return') void this._startPaymentReturn();
      }
      if (!email && !this._ssoEmail && this._path.startsWith('/orders')) this._openLogin('/orders');
    });
    // Arrived via an email sign-in link? Complete it — onAuthChange above
    // then picks up the signed-in user and warms the gateway session.
    void this._completeEmailLinkIfPresent();
    // FCM cannot raise a system notification while the page is focused — show
    // the gateway's order updates as in-app toasts instead.
    this._pushUnsub = onPushMessage((msg) => {
      this._toastMsg(msg.body || msg.title);
      if (this._path.startsWith('/orders')) void this._loadOrders();
    });
    this._routeChanged();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
    window.removeEventListener('mall-cart-changed', this._onCartChanged);
    window.removeEventListener(SAVED_OUTLETS_CHANGED_EVENT, this._onSavedOutletsChanged);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this._outletsTimer !== undefined) {
      window.clearInterval(this._outletsTimer);
      this._outletsTimer = undefined;
    }
    if (this._outletSearchTimer !== undefined) {
      window.clearTimeout(this._outletSearchTimer);
      this._outletSearchTimer = undefined;
    }
    this._cancelPaymentReturnPoll();
    this._stopScanCamera();
    this._pushUnsub();
    this._pushUnsub = () => undefined;
    super.disconnectedCallback();
  }

  private _onPopState = () => {
    this._path = window.location.pathname;
    this._query = new URLSearchParams(window.location.search);
    this._routeChanged();
  };

  /** Wheel over a one-line row (.chips / .cat-tiles) scrolls it sideways.
   *  A mouse wheel only emits deltaY, so without this a desktop shopper cannot
   *  reach the pills or tiles that are out of view. The event is consumed only
   *  while the row can move in that direction — at either end the page keeps
   *  scrolling normally. */
  private _onRowWheel = (e: WheelEvent) => {
    const row = e.currentTarget as HTMLElement;
    const max = row.scrollWidth - row.clientWidth;
    if (max <= 0) return;
    // deltaMode: 1 = lines (Firefox mouse wheel), 2 = pages.
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? row.clientWidth : 1;
    const delta = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * unit;
    if (!delta) return;
    const next = Math.max(0, Math.min(max, row.scrollLeft + delta));
    if (next === row.scrollLeft) return;
    e.preventDefault();
    row.scrollLeft = next;
  };

  private _onCartChanged = () => {
    this._cart = loadCart();
  };

  /** Favourites / recents were written (this tab or another tab). */
  private _onSavedOutletsChanged = () => {
    this._favOutlets = loadFavouriteOutlets();
    this._recentOutlets = loadRecentOutlets();
  };

  private _onVisibilityChange = () => {
    if (document.hidden) return;
    // Tab became visible again — refresh outlet availability right away.
    void this._refreshOutlets();
    // Back on the payment confirmation screen: ask the gateway now instead of
    // waiting for the next scheduled poll (the provider may have confirmed
    // while the tab was in the background).
    if (this._path.startsWith('/payment/return') && this._payReturnStatus === 'verifying') {
      void this._startPaymentReturn();
    }
  };

  private _navigate(path: string) {
    window.history.pushState({}, '', path);
    this._path = window.location.pathname;
    this._query = new URLSearchParams(window.location.search);
    this._routeChanged();
  }

  private _routeChanged() {
    this._stopScanCamera();
    this._scanResult = null;
    // Leaving the confirmation screen abandons its polling loop.
    if (!this._path.startsWith('/payment/return')) this._cancelPaymentReturnPoll();
    if (!this._categories.length && !this._categoriesLoading) void this._loadCategories();
    // Deep links (/s/<code>, /p/..., /checkout) start with no outlet list —
    // fetch it so names and online state render without waiting for the poll.
    if (!this._outlets.length) void this._refreshOutlets();
    const seg = this._path.split('/').filter(Boolean);

    if (seg.length === 0 || seg[0] === '') {
      const needLoad = this._homeDirty || !this._homeItems.length;
      if (needLoad && !this._homeLoading) this._loadHome(1);
      this._homeDirty = false;
      if (!this._outlets.length) void this._refreshOutlets();
    } else if (seg[0] === 'search') {
      const q = this._query.get('q') || '';
      this._searchInput = q;
      this._runSearch(q, 1);
    } else if (seg[0] === 'c' && seg[1]) {
      this._catStore = this._query.get('store') || '';
      void this._loadCategory(seg[1], 1);
    } else if (seg[0] === 'outlets') {
      this._outletQuery = this._query.get('q') || '';
      const radius = Number(this._query.get('radius') || '25');
      this._outletRadius = Number.isFinite(radius) && radius > 0 ? radius : 25;
      this._outletNear = this._query.get('near') === '1';
      this._outletGeoError = '';
      if (this._outletNear && !this._outletCoords) {
        void this._locateThenLoadOutlets();
      } else if (this._outletSearchActive) {
        void this._fetchOutlets(1, true);
      } else {
        // Idle: no query and no location — never list every outlet.
        this._outletResults = [];
        this._outletTotal = 0;
      }
    } else if (seg[0] === 's' && seg[1] && seg[2] === 'scan') {
      this._recordOutletView(seg[1]);
      if (!this._email) {
        this._openLogin(this._path);
      } else {
        void this._loadProfile(seg[1]);
      }
    } else if (seg[0] === 's' && seg[1]) {
      this._recordOutletView(seg[1]);
      this._loadStore(seg[1]);
    } else if (seg[0] === 'p' && seg[1] && seg[2]) {
      this._loadProduct(seg[1], seg[2]);
    } else if (seg[0] === 'orders') {
      if (this._email) this._loadOrders();
      else this._openLogin('/orders');
    } else if (seg[0] === 'checkout') {
      if (this._cart) void this._loadPaymentOptions(this._cart.storeCode, this._cartSubtotal());
      if (this._email && this._cart) void this._loadProfile(this._cart.storeCode);
    } else if (seg[0] === 'payment' && seg[1] === 'return') {
      this._startPaymentReturn();
    }
  }

  private async _warmGatewaySession() {
    try {
      const token = await getIdToken(true);
      if (!token) return;
      await api.loginGateway(token);
    } catch {
      /* federation sessions auto-recover per request — ignore */
    }
  }

  /**
   * Confirm the session key handed over by the POS app still resolves to an
   * account. A gateway restart drops the in-memory session store, so this is
   * the only way to know whether the shopper is really signed in.
   */
  private async _verifyHandoffSession(sessionKey: string) {
    try {
      const session = await api.gatewaySession();
      if (!session?.email) throw new Error('No session');
      // A real Firebase sign-in may have landed while we were validating; it
      // wins, and the handed-over key must not be re-installed behind its back.
      if (this._firebaseEmail) {
        this._clearHandoffSession();
        return;
      }
      this._ssoEmail = session.email;
      setHandoffSession({ sessionKey, email: session.email });
      // The optimistic email (if any) already drove the first route pass; only
      // re-route when we only now learned who the shopper is.
      if (!this._email) {
        this._email = session.email;
        this._routeChanged();
      }
    } catch {
      // Expired / gateway restarted → forget it and fall back to sign-in.
      this._clearHandoffSession();
      if (!this._firebaseEmail) this._email = null;
      if (this._path.startsWith('/orders')) this._openLogin('/orders');
    }
  }

  private _clearHandoffSession() {
    setHandoffSession(null);
    this._ssoEmail = null;
  }

  /** Completes a pending email-link sign-in when this page was opened from
   *  the link. Errors surface in the login modal. */
  private async _completeEmailLinkIfPresent() {
    try {
      if (!isSignInWithEmailLink()) return;
      const email = await completeEmailLinkSignIn();
      if (email) {
        this._loginOpen = false;
        this._toastMsg(`Signed in as ${email}`);
      }
    } catch (err: any) {
      this._loginError = err.message || 'Email link sign-in failed';
      this._loginOpen = true;
    }
  }

  private _toastMsg(msg: string) {
    this._toast = msg;
    window.setTimeout(() => {
      if (this._toast === msg) this._toast = '';
    }, 2600);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Data loading
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Fetch the outlet list and refresh the availability state in place.
   * Called when the home page loads, every 15s, on window focus and after
   * any failed store API call — so outlets flip to "offline" (and back)
   * on screen without a reload.
   */
  private async _refreshOutlets() {
    if (this._outletsRefreshBusy) return;
    this._outletsRefreshBusy = true;
    try {
      const outlets = await api.getOutlets();
      // If the outlet currently being viewed just went offline, tell the shopper.
      const storeCode = this._currentStoreCode();
      if (storeCode) {
        const before = this._outlets.find((o) => o.code === storeCode);
        const after = outlets.find((o) => o.code === storeCode);
        if (before && before.online !== false && after && after.online === false) {
          this._toastMsg(`🏪 ${after.name} is now offline — showing the last synced catalogue; checkout opens when it reconnects`);
        }
      }
      this._outlets = outlets;
      // Deep-linked saved outlets may still carry their code as the name —
      // upgrade them now that the poll knows the real names.
      refreshSavedOutletNames(outlets);
      this._syncCatalogWithOutlets(outlets);
    } catch {
      /* transient — keep the previous list */
    } finally {
      this._outletsRefreshBusy = false;
    }
  }

  /**
   * The outlets poll doubles as the mall's "catalogue changed" signal: it
   * carries each outlet's published-item count and the catalog `rev`
   * (server-side content revision — bumps on any change, including image
   * uploads). When the signature changes — an item was published/unpublished,
   * an image was added/removed, or an outlet flipped online/offline — refresh
   * the catalogue the shopper is looking at, so the item grids follow the
   * chip counters instead of staying stale. It also retries the Home load
   * when it came back empty (first load racing a gateway/tunnel warm-up), and
   * marks Home dirty when changes were seen while on another page.
   */
  private _syncCatalogWithOutlets(outlets: Array<{ code: string; itemCount: number; online: boolean; rev?: number }>) {
    const next = new Map<string, string>();
    for (const o of outlets) {
      next.set(o.code, `${o.itemCount ?? 0}:${o.online === false ? 'off' : 'on'}:${o.rev ?? 0}`);
    }
    const prev = this._outletSig;
    this._outletSig = next;

    // Which outlets changed between polls? (The first poll has no baseline —
    // not a change.)
    let changed = false;
    for (const [code, sig] of next) {
      if (prev.has(code) && prev.get(code) !== sig) changed = true;
    }

    const seg = this._path.split('/').filter(Boolean);

    if (seg.length === 0) {
      // Home — live-refresh the item grid when anything moved; also retry an
      // empty/error load so shoppers never need to tap Home to see items.
      const hasItems = outlets.some((o) => (o.itemCount ?? 0) > 0);
      this._homeDirty = false;
      if (changed || this._homeError || (this._homeItems.length === 0 && hasItems)) {
        if (changed) void this._loadCategories();  // refresh tile counts
        void this._loadHome(1);
      }
      return;
    }

    if (seg[0] === 's' && seg[1] && !seg[2]) {
      // Outlet page — reload only when THIS outlet's items or availability
      // moved (e.g. publishing while the outlet page is open).
      const code = this._currentStoreCode();
      const before = prev.get(code);
      const after = next.get(code);
      if (before !== undefined && after !== undefined && before !== after) {
        void this._loadStore(code);
      }
      return;
    }

    if (seg[0] === 'c' && seg[1]) {
      // Category page — any catalog change (publish/unpublish, price, image,
      // category edit) should refresh the visible grid.
      if (changed && !this._catLoading) void this._loadCategory(seg[1], 1);
      return;
    }

    // Anywhere else — remember to refresh Home when it is next visited and
    // keep the category tile counts fresh while browsing.
    if (changed) {
      void this._loadCategories();
      this._homeDirty = true;
    }
  }

  /** Store code of the page currently being viewed ('/s/{code}...' or '/p/{code}/...'). */
  private _currentStoreCode(): string {
    const seg = this._path.split('/').filter(Boolean);
    if ((seg[0] === 's' || seg[0] === 'p') && seg[1]) {
      try {
        return decodeURIComponent(seg[1]);
      } catch {
        return seg[1];
      }
    }
    return '';
  }

  /** True when the outlet is marked offline by the latest availability poll. */
  private _isOutletOffline(storeCode: string): boolean {
    const outlet = this._outlets.find((o) => o.code === storeCode);
    return !!outlet && outlet.online === false;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Outlet discovery (/outlets)
  // ─────────────────────────────────────────────────────────────────────

  private _requestGeolocation(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
      );
    });
  }

  private async _locateThenLoadOutlets() {
    const coords = await this._requestGeolocation();
    if (!coords) {
      this._outletNear = false;
      this._outletCoords = null;
      this._outletGeoError =
        'Location is unavailable (permission denied or no GPS). Search by outlet name or area instead.';
      this._outletResults = [];
      this._outletTotal = 0;
      this._syncOutletsUrl();
      this._toastMsg('Could not get your location — search by outlet name or area');
      return;
    }
    this._outletCoords = coords;
    this._outletGeoError = '';
    this._syncOutletsUrl();
    void this._fetchOutlets(1, true);
  }

  private async _toggleNearMe() {
    if (this._outletNear) {
      this._outletNear = false;
      this._syncOutletsUrl();
      if (this._outletSearchActive) {
        void this._fetchOutlets(1, true);
      } else {
        this._outletResults = [];
        this._outletTotal = 0;
      }
      return;
    }
    const coords = await this._requestGeolocation();
    if (!coords) {
      this._outletGeoError =
        'Location is unavailable (permission denied or no GPS). Search by outlet name or area instead.';
      this._toastMsg('Could not get your location — search by outlet name or area');
      return;
    }
    this._outletCoords = coords;
    this._outletNear = true;
    this._outletGeoError = '';
    this._syncOutletsUrl();
    void this._fetchOutlets(1, true);
  }

  private _onOutletQueryInput(value: string) {
    this._outletQuery = value;
    this._outletGeoError = '';
    this._syncOutletsUrl();
    if (this._outletSearchTimer !== undefined) window.clearTimeout(this._outletSearchTimer);
    this._outletSearchTimer = window.setTimeout(() => {
      this._outletSearchTimer = undefined;
      if (this._outletSearchActive) {
        void this._fetchOutlets(1, true);
      } else {
        this._outletResults = [];
        this._outletTotal = 0;
      }
    }, 300);
  }

  private _onOutletRadiusChange(value: number) {
    this._outletRadius = value;
    this._syncOutletsUrl();
    void this._fetchOutlets(1, true);
  }

  private _syncOutletsUrl() {
    const params = new URLSearchParams();
    if (this._outletQuery.trim()) params.set('q', this._outletQuery.trim());
    if (this._outletNear) {
      params.set('near', '1');
      params.set('radius', String(this._outletRadius));
    }
    const search = params.toString();
    window.history.replaceState({}, '', `/outlets${search ? `?${search}` : ''}`);
    this._query = new URLSearchParams(window.location.search);
  }

  private async _fetchOutlets(page: number, reset: boolean) {
    // Guard: never request the full outlet list. Results require either a
    // name/area query or an acquired location.
    if (!this._outletSearchActive) {
      this._outletResults = [];
      this._outletTotal = 0;
      this._outletPage = 1;
      this._outletLoading = false;
      return;
    }
    const seq = ++this._outletSearchSeq;
    this._outletLoading = true;
    this._outletError = '';
    try {
      const near = this._outletNear && this._outletCoords;
      const res = await api.searchOutlets({
        q: this._outletQuery.trim() || undefined,
        lat: near ? this._outletCoords!.lat : undefined,
        lng: near ? this._outletCoords!.lng : undefined,
        radiusKm: near ? this._outletRadius : undefined,
        page,
        pageSize: 20,
      });
      if (seq !== this._outletSearchSeq) return;  // a newer search won
      this._outletResults = reset ? res.outlets : [...this._outletResults, ...res.outlets];
      this._outletTotal = res.total;
      this._outletPage = res.page;
    } catch (err: any) {
      if (seq === this._outletSearchSeq) {
        this._outletError = err.message || 'Could not load outlets';
      }
    } finally {
      if (seq === this._outletSearchSeq) this._outletLoading = false;
    }
  }

  private _outletDistanceLabel(outlet: { distanceKm?: number }): string {
    if (outlet.distanceKm === undefined) return '';
    return outlet.distanceKm < 1
      ? `${Math.round(outlet.distanceKm * 1000)} m`
      : `${outlet.distanceKm.toFixed(1)} km`;
  }

  private _openOutlet(outlet: { code: string; name: string; online?: boolean }) {
    // Offline outlets stay browsable from their last synced catalogue.
    this._navigate(`/s/${encodeURIComponent(outlet.code)}`);
  }

  /** Remember this device browsed an outlet (Home / Outlets "Recently viewed"). */
  private _recordOutletView(code: string) {
    let decoded = code;
    try {
      decoded = decodeURIComponent(code);
    } catch {
      /* keep the raw segment */
    }
    const live = this._outlets.find((o) => o.code.toLowerCase() === decoded.toLowerCase());
    const known = this._favOutlets.find((o) => o.code.toLowerCase() === decoded.toLowerCase())
      ?? this._recentOutlets.find((o) => o.code.toLowerCase() === decoded.toLowerCase());
    recordOutletView({ code: live?.code || decoded, name: live?.name || known?.name || decoded });
  }

  private _toggleFavourite(outlet: SavedOutlet) {
    const added = toggleFavouriteOutlet(outlet);
    this._toastMsg(added ? `⭐ ${outlet.name} added to favourites` : `${outlet.name} removed from favourites`);
  }

  /** Live availability for a saved outlet, falling back to the stored name
   *  (an outlet may not be in the current list, e.g. nothing published). */
  private _mergeSavedOutlet(saved: SavedOutlet): OutletRowData {
    const live = this._outlets.find((o) => o.code.toLowerCase() === saved.code.toLowerCase());
    if (!live) return { code: saved.code, name: saved.name };
    return {
      code: live.code,
      name: live.name || saved.name,
      location: live.location,
      online: live.online,
      itemCount: live.itemCount,
      distanceKm: live.distanceKm,
    };
  }

  private async _loadCategories() {
    if (this._categoriesLoading) return;
    this._categoriesLoading = true;
    this._categoriesError = '';
    try {
      this._categories = await api.getCategories();
      // The category page may have loaded before the names arrived — refresh
      // it once so the title/chips render instead of a bare slug.
      if (this._catSlug && this._path.startsWith('/c/') && !this._catLoading) {
        void this._loadCategory(this._catSlug, 1);
      }
    } catch (err: any) {
      this._categoriesError = err.message || 'Could not load categories';
    } finally {
      this._categoriesLoading = false;
    }
  }

  private async _loadCategory(slug: string, page: number) {
    this._catSlug = slug;
    this._catLoading = true;
    this._catError = '';
    if (page === 1) {
      this._catItems = [];
      this._catTotal = 0;
    }
    try {
      const res = await api.getCatalog({
        category: slug,
        store: this._catStore || undefined,
        page,
        pageSize: 24,
      });
      this._catItems = page === 1 ? res.items : [...this._catItems, ...res.items];
      this._catTotal = res.total;
      this._catPage = page;
    } catch (err: any) {
      this._catError = err.message || 'Could not load this category';
    } finally {
      this._catLoading = false;
    }
  }

  private async _loadHome(page: number) {
    this._homeLoading = true;
    this._homeError = '';
    try {
      const res = await api.getCatalog({ page, pageSize: 24 });
      this._homeItems = page === 1 ? res.items : [...this._homeItems, ...res.items];
      this._homeTotal = res.total;
      this._homePage = page;
    } catch (err: any) {
      this._homeError = err.message || 'Could not load the mall';
    } finally {
      this._homeLoading = false;
    }
  }

  private async _runSearch(q: string, page: number) {
    this._queryText = q;
    this._searchLoading = true;
    try {
      const res = await api.getCatalog({ search: q, page, pageSize: 24 });
      this._searchItems = page === 1 ? res.items : [...this._searchItems, ...res.items];
      this._searchTotal = res.total;
      this._searchPage = page;
    } catch (err: any) {
      this._toastMsg(err.message || 'Search failed');
    } finally {
      this._searchLoading = false;
    }
  }

  private async _loadStore(storeCode: string) {
    this._storeLoading = true;
    try {
      const res = await api.getCatalog({ store: storeCode, pageSize: 60 });
      this._storeItems = res.items;
      // Deep links have no outlet name yet (the availability poll may not have
      // run) — the catalogue carries it, so upgrade the saved-list entry.
      const storeName = res.items.find((i) => i.storeName)?.storeName;
      if (storeName) recordOutletView({ code: storeCode, name: storeName });
    } catch (err: any) {
      this._toastMsg(err.message || 'Could not load this outlet');
      void this._refreshOutlets(); // failure may mean the outlet just went offline
    } finally {
      this._storeLoading = false;
    }
  }

  private async _loadProduct(storeCode: string, stockId: string) {
    this._productLoading = true;
    this._product = null;
    try {
      this._product = await api.getItem(storeCode, stockId);
      this._productQty = 1;
    } catch (err: any) {
      this._toastMsg(err.message || 'Item not available');
      void this._refreshOutlets(); // failure may mean the outlet just went offline
      this._navigate('/');
    } finally {
      this._productLoading = false;
    }
  }

  private async _loadOrders() {
    this._ordersLoading = true;
    this._ordersError = '';
    try {
      this._orders = await api.myOrders();
    } catch (err: any) {
      this._ordersError = err.message || 'Could not load your orders';
    } finally {
      this._ordersLoading = false;
    }
  }

  private _cancelPaymentReturnPoll() {
    this._payReturnSeq += 1;
    if (this._payReturnTimer !== undefined) {
      window.clearTimeout(this._payReturnTimer);
      this._payReturnTimer = undefined;
    }
  }

  /** `/payment/return?order=<gatewayOrderId>`
   *
   * Confirmation is driven by requerying THAT order — the gateway enforces
   * ownership — never by whatever a provider callback claims. The waiting
   * screen keeps "Return to My Orders" disabled until the gateway confirms. */
  private async _startPaymentReturn() {
    this._cancelPaymentReturnPoll();
    const seq = this._payReturnSeq;

    const queryOrder = this._query.get('order') || '';
    const hint = loadPendingPayment();
    this._payReturnOrderId = queryOrder || (hint?.orderId ?? '');
    if (hint && hint.orderId === this._payReturnOrderId) {
      this._payReturnProvider = hint.provider || this._payReturnProvider;
      this._payReturnRefNo = hint.refNo || this._payReturnRefNo;
    }
    if (!this._payReturnOrderId) {
      this._payReturnStatus = 'error';
      this._payReturnDetail = 'This payment link is missing its order reference.';
      return;
    }

    if (!this._email) {
      // The provider redirects back BEFORE Firebase restores the session, so
      // wait briefly instead of declaring the payment missing.
      if (!this._authReady && this._payReturnAuthWaits < 50) {
        this._payReturnAuthWaits += 1;
        this._payReturnStatus = 'verifying';
        this._payReturnTimer = window.setTimeout(() => {
          if (seq === this._payReturnSeq && this._path.startsWith('/payment/return')) {
            void this._startPaymentReturn();
          }
        }, 300);
        return;
      }
      this._payReturnStatus = 'signin';
      return;
    }

    this._payReturnAuthWaits = 0;
    this._payReturnStatus = 'verifying';
    this._payReturnDetail = '';
    this._payReturnCancelable = true;
    await this._pollPaymentReturn(seq, Date.now() + PAY_RETURN_MAX_WAIT_MS);
  }

  private async _pollPaymentReturn(seq: number, deadline: number) {
    if (seq !== this._payReturnSeq) return;
    try {
      const res = await api.requeryOrder(this._payReturnOrderId);
      if (seq !== this._payReturnSeq) return;
      if (res.provider) this._payReturnProvider = res.provider;
      if (res.refNo) this._payReturnRefNo = res.refNo;

      const status = (res.status || '').toLowerCase();
      if (status === 'paid' || status === 'completed' || status === 'ready') {
        this._payReturnStatus = 'paid';
        clearPendingPayment(this._payReturnOrderId);
        this._payReturnTimer = window.setTimeout(() => {
          if (seq === this._payReturnSeq) this._navigate('/orders');
        }, 1500);
        return;
      }
      if (status === 'cancelled') {
        this._payReturnStatus = 'cancelled';
        this._payReturnCancelable = false;
        this._payReturnDetail = 'This order was cancelled.';
        clearPendingPayment(this._payReturnOrderId);
        return;
      }
      if (status === 'failed' || status === 'expired') {
        this._payReturnStatus = 'unconfirmed';
        this._payReturnCancelable = false;
        this._payReturnDetail = status === 'expired'
          ? 'This payment window expired — nothing was charged.'
          : 'The payment did not go through.';
        clearPendingPayment(this._payReturnOrderId);
        return;
      }
    } catch (err: any) {
      if (seq !== this._payReturnSeq) return;
      // 401 means the session is not ready yet; every other error (outlet
      // offline, provider hiccup) is retried until the deadline.
      if (err?.status === 401) this._payReturnStatus = 'verifying';
    }

    if (Date.now() >= deadline) {
      this._payReturnStatus = 'unconfirmed';
      this._payReturnDetail = '';
      return;
    }
    this._payReturnTimer = window.setTimeout(() => {
      if (seq === this._payReturnSeq && this._path.startsWith('/payment/return')) {
        void this._pollPaymentReturn(seq, deadline);
      }
    }, PAY_RETURN_POLL_MS);
  }

  private async _cancelPendingOrder() {
    if (!this._payReturnOrderId || this._payReturnCancelBusy) return;
    if (!window.confirm('Cancel this order?')) return;
    this._payReturnCancelBusy = true;
    this._cancelPaymentReturnPoll();
    try {
      await api.cancelOrder(this._payReturnOrderId);
      clearPendingPayment(this._payReturnOrderId);
      this._payReturnStatus = 'cancelled';
      this._payReturnCancelable = false;
      this._payReturnDetail = 'Order cancelled.';
      this._toastMsg('Order cancelled');
    } catch (err: any) {
      // Most likely the payment landed first — resume checking so the shopper
      // sees the confirmed state instead of a stale error.
      this._toastMsg(err.message || 'Could not cancel the order');
      void this._startPaymentReturn();
    } finally {
      this._payReturnCancelBusy = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Auth modal
  // ─────────────────────────────────────────────────────────────────────

  private _openLogin(returnTo: string) {
    this._loginReturn = returnTo || this._path;
    this._loginOpen = true;
    this._loginError = '';
    this._loginLinkSent = false;
  }

  // Passwordless: no password is ever collected. Google sign-in is a popup;
  // the email option sends a one-time sign-in link and the user clicks it.
  private async _sendSignInLink() {
    const email = this._loginEmail.trim();
    if (!email) {
      this._loginError = 'Please enter your email address.';
      return;
    }
    this._loginBusy = true;
    this._loginError = '';
    try {
      await sendSignInLink(email);
      this._loginLinkSent = true;
    } catch (err: any) {
      if (err.code === 'auth/invalid-email') {
        this._loginError = 'Invalid email address.';
      } else if (err.code === 'auth/unauthorized-continue-uri') {
        this._loginError = 'This domain is not authorized for email link sign-in. Check Firebase Console → Auth → Sign-in method → Email link.';
      } else {
        this._loginError = 'Failed to send the link: ' + (err.message || 'Unknown error');
      }
    } finally {
      this._loginBusy = false;
    }
  }

  private async _doGoogleLogin() {
    this._loginBusy = true;
    this._loginError = '';
    try {
      await signInWithGoogle();
      this._loginOpen = false;
      this._navigate(this._loginReturn);
    } catch (err: any) {
      this._loginError = err.message || 'Google sign-in failed';
    } finally {
      this._loginBusy = false;
    }
  }

  private async _doLogout() {
    // Stop pushes for this account before the session goes away — otherwise a
    // shared device keeps receiving the previous shopper's order updates.
    void disablePush();
    // A handed-over session belongs to the POS app too, so it is only
    // forgotten here — never call /auth/logout, which would sign the user out
    // of the POS app as well.
    if (this._ssoEmail) this._clearHandoffSession();
    else await signOut();
    this._firebaseEmail = null;
    this._email = null;
    this._orders = [];
    this._navigate('/');
  }

  // ─────────────────────────────────────────────────────────────────────
  // Cart actions
  // ─────────────────────────────────────────────────────────────────────

  private _addItem(item: MallItem, qty = 1) {
    const storeCode = item.storeCode;
    const storeName = item.storeName || storeCode;
    const result = addToCart(
      { code: storeCode, name: storeName },
      {
        stockId: item.id,
        code: item.code,
        name: item.name,
        price: item.price,
        imageUrl: item.thumbnailUrl || item.imageUrl,
      },
      qty,
    );
    if (result === 'conflict') {
      const ok = window.confirm(
        `Your cart contains items from another outlet.\n\n${siteBrand()} Mall carts hold one outlet at a time (each outlet receives its own payment). Start a new cart with this item?`,
      );
      if (ok) {
        saveCart(null);
        addToCart({ code: storeCode, name: storeName }, {
          stockId: item.id, code: item.code, name: item.name, price: item.price,
          imageUrl: item.thumbnailUrl || item.imageUrl,
        }, qty);
        this._cart = loadCart();
        this._toastMsg('New cart started');
        return;
      }
      return;
    }
    this._cart = loadCart();
    this._toastMsg('Added to cart');
  }

  private _cartSubtotal(): number {
    return this._cart ? this._cart.items.reduce((n, i) => n + i.price * i.quantity, 0) : 0;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Checkout
  // ─────────────────────────────────────────────────────────────────────

  /** Who owns the signed-in email at this outlet? Cached per email+outlet. */
  private async _loadProfile(storeCode: string) {
    if (!this._email || !storeCode) return;
    const key = `${storeCode}|${this._email}`;
    if (this._profileKey === key) return;
    this._profileLoading = true;
    try {
      const profile = await api.profile(storeCode);
      this._profileKey = key;
      this._profile = profile;
      if (profile.found) {
        this._shopperType = profile.shopperType === 'customer' ? 'customer' : 'member';
        if (profile.name) this._checkoutName = profile.name;
        if (profile.phone) this._checkoutPhone = profile.phone;
        this._checkoutCompany = profile.companyName || '';
        this._checkoutAddress = profile.address || '';
      }
    } catch {
      // Outlet unreachable — let checkout report it; keep the sign-up form.
      this._profile = null;
      this._profileKey = '';
    } finally {
      this._profileLoading = false;
    }
  }

  /**
   * Does this outlet accept online payment? The mall hides the "pay online"
   * choice until the outlet has enabled toyyibPay and stored its category code
   * + secret key (PWA → Mall Publishing → Payment settings).
   */
  private async _loadPaymentOptions(storeCode: string, total?: number) {
    if (!storeCode) return;
    const key = storeCode + '|' + (total ?? '');
    if (this._paymentOptionsKey === key) return;
    this._paymentOptionsKey = key;
    try {
      const res = await api.paymentOptions(storeCode, total);
      this._onlinePayment = !!res.onlinePayment;
      this._onlinePaymentNote = res.message || '';
      this._paymentProviderLabel = res.providerLabel || '';
      this._paymentFeeMode = res.feeMode || 'outlet';
      this._paymentFee = res.feeAmount || 0;
      this._paymentCustomerTotal = res.customerTotal || total || 0;
    } catch {
      // Outlet unreachable — checkout reports it; never offer online payment.
      this._onlinePayment = false;
      this._onlinePaymentNote = 'Online payment could not be checked';
    }
    if (!this._onlinePayment && this._checkoutPayment === 'online') {
      this._checkoutPayment = 'counter';
    }
  }

  /** True when the signed-in email is already a complete member/customer. */
  private _profileKnown(): boolean {
    const profile = this._profile;
    if (!profile?.found) return false;
    // A record without a phone (or company for a wholesale customer) still
    // needs the details form — otherwise checkout would block with nothing
    // to fix.
    if (!this._checkoutName.trim() || !this._checkoutPhone.trim()) return false;
    if (this._shopperType === 'customer' && !this._checkoutCompany.trim()) return false;
    return true;
  }

  private _shopperPayload() {
    return {
      shopperType: this._shopperType,
      name: this._checkoutName.trim(),
      companyName: this._checkoutCompany.trim(),
      address: this._checkoutAddress.trim(),
      phone: this._checkoutPhone.trim(),
    };
  }

  private _validateShopper(): string {
    if (!this._checkoutName.trim() || !this._checkoutPhone.trim()) {
      return 'Please enter your name and phone number';
    }
    if (!this._profileKnown() && this._shopperType === 'customer' && !this._checkoutCompany.trim()) {
      return 'Please enter your company name';
    }
    return '';
  }

  private async _submitCheckout() {
    const cart = this._cart;
    if (!cart) return;
    if (!this._email) {
      this._openLogin('/checkout');
      return;
    }
    const invalid = this._validateShopper();
    if (invalid) {
      this._checkoutError = invalid;
      return;
    }
    this._checkoutBusy = true;
    this._checkoutError = '';
    try {
      const res = await api.checkout({
        storeCode: cart.storeCode,
        items: cart.items.map((i) => ({ stockId: i.stockId, quantity: i.quantity })),
        payment: this._checkoutPayment,
        ...this._shopperPayload(),
      });
      saveCart(null);
      this._cart = null;
      this._profileKey = ''; // the shopper record may have just been created
      if (this._checkoutPayment === 'online' && res.paymentUrl) {
        // Remember who is being paid so the return page can name the provider
        // before its first status poll comes back.
        savePendingPayment({
          orderId: res.id,
          provider: res.paymentProvider || '',
          refNo: res.refNo,
          at: Date.now(),
        });
        window.location.href = res.paymentUrl;
        return;
      }
      clearPendingPayment();
      if (res.warning) this._toastMsg(res.warning);
      if (res.ordersQueued || res.status === 'pending_confirmation' || res.status === 'queued') {
        this._toastMsg((res as any).message
          || 'Order saved — we will notify you as soon as the outlet reconnects.');
      }
      this._navigate('/orders');
    } catch (err: any) {
      this._checkoutError = err.message || 'Checkout failed';
      if (err.code === 'PROFILE_REQUIRED') {
        this._profile = { found: false, shopperType: '', name: '', companyName: '', address: '', phone: '' };
        this._profileKey = '';
      }
      if (err.code === 'ONLINE_PAYMENT_UNAVAILABLE') {
        // The outlet lost (or never had) its toyyibPay setup — fall back to
        // paying at the counter and hide the option.
        this._onlinePayment = false;
        this._onlinePaymentNote = err.message || '';
        this._checkoutPayment = 'counter';
      }
      void this._refreshOutlets(); // failure may mean the outlet just went offline
    } finally {
      this._checkoutBusy = false;
    }
  }

  /**
   * Complete an order that was saved while the outlet was offline: the outlet
   * re-checks availability, the counter hold is created and (for online
   * payment) the bill is raised. The shopper picks the payment method here.
   */
  private async _confirmOrder(orderId: string, payment: 'counter' | 'online') {
    if (this._confirmBusy) return;
    this._confirmBusy = orderId;
    try {
      const res = await api.confirmOrder(orderId, payment);
      if (res.paymentUrl) {
        savePendingPayment({
          orderId: res.id,
          provider: res.paymentProvider || '',
          refNo: res.refNo,
          at: Date.now(),
        });
        window.location.href = res.paymentUrl;
        return;
      }
      if (res.warning) this._toastMsg(res.warning);
      else this._toastMsg(res.status === 'pending'
        ? 'Order confirmed — complete the payment to reserve it.'
        : 'Order confirmed — pay at the counter on collection.');
      await this._loadOrders();
    } catch (err: any) {
      const code = err?.code;
      if (code === 'ORDER_UNAVAILABLE' || code === 'OUTLET_OFFLINE') {
        this._toastMsg(err.message || 'The outlet is not available right now.');
        await this._loadOrders();
      } else {
        this._toastMsg(err?.message || 'Could not confirm the order.');
      }
    } finally {
      this._confirmBusy = '';
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // In-store scan & go
  // ─────────────────────────────────────────────────────────────────────

  private _scanTotal(): number {
    return this._scanLines.reduce((n, l) => n + l.price * l.quantity, 0);
  }

  private async _startScanCamera(storeCode: string) {
    this._scanMessage = '';
    this._scanSupported = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      this._scanSupported = false;
      this._scanMessage = 'Camera scanning is not supported by this browser — enter the barcode manually below.';
      return;
    }

    this._stopScanCamera(); // reset any previous run
    this._scanCooldown = 0;
    const startId = ++this._scanStartId;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
    } catch {
      this._scanMessage = 'Camera unavailable — check the camera permission, or enter the barcode manually below.';
      return;
    }
    if (startId !== this._scanStartId) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    this._scanStream = stream;
    this._scanCameraOn = true;
    await this.updateComplete;
    const video = this._videoEl;
    if (!video || startId !== this._scanStartId) return;
    video.srcObject = stream;
    try {
      await video.play();
    } catch {
      /* Autoplay can be rejected until frames arrive; the decoder retries playback. */
    }

    // Prefer the native detector (hardware accelerated) when the platform
    // truly supports it. `BarcodeDetector` can exist and still fail on every
    // frame (desktop builds, some WebViews), so ZXing is the safety net.
    if (await this._startNativeScanner(video, storeCode, startId)) return;
    await this._startZxingScanner(video, storeCode, startId);
  }

  /** Native BarcodeDetector path. Returns false when this platform cannot
   *  decode the formats we need, letting the caller fall back to ZXing. */
  private async _startNativeScanner(video: HTMLVideoElement, storeCode: string, startId: number): Promise<boolean> {
    const Detector = window.BarcodeDetector;
    if (!Detector || typeof Detector.getSupportedFormats !== 'function') return false;
    let supported: string[] = [];
    try {
      supported = await Detector.getSupportedFormats();
    } catch {
      return false;
    }
    if (startId !== this._scanStartId || !this._scanStream) return false;
    const wanted = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'code_93', 'itf', 'qr_code'];
    // Never pass an unsupported format: the constructor throws for the whole list.
    const formats = wanted.filter((f) => supported.includes(f));
    if (formats.length === 0) return false;
    let detector: { detect: (el: HTMLVideoElement) => Promise<Array<{ rawValue?: string }>> };
    try {
      detector = new Detector({ formats });
    } catch {
      return false;
    }

    this._scanTimer = window.setInterval(() => {
      if (startId !== this._scanStartId || this._scanCooldown > Date.now()) return;
      const el = this._videoEl;
      if (!el || el.readyState < 2) return;
      detector.detect(el)
        .then((codes) => {
          if (codes && codes.length > 0) this._handleScanDetected(storeCode, codes[0].rawValue || '');
        })
        .catch(() => { /* frame errors are normal */ });
    }, 250);

    // Some Chromium builds expose BarcodeDetector but never return a result.
    // If nothing was decoded after a few seconds, switch to the JS decoder.
    this._scanFallbackTimer = window.setTimeout(() => {
      this._scanFallbackTimer = undefined;
      if (startId !== this._scanStartId || this._scanFoundAnything) return;
      if (this._scanTimer !== undefined) {
        window.clearInterval(this._scanTimer);
        this._scanTimer = undefined;
      }
      void this._startZxingScanner(video, storeCode, startId);
    }, 6000);
    return true;
  }

  /** Cross-browser fallback: ZXing decodes QR + the usual retail barcodes
   *  from the live camera stream (iOS Safari, Firefox, desktop, WebViews). */
  private async _startZxingScanner(video: HTMLVideoElement, storeCode: string, startId: number) {
    try {
      const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
        import('@zxing/browser'),
        import('@zxing/library'),
      ]);
      if (startId !== this._scanStartId || !this._scanStream) return;
      const hints = new Map<any, any>();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.QR_CODE,
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.ITF,
      ]);
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 120,
        delayBetweenScanSuccess: 900,
      });
      const controls = await reader.decodeFromStream(this._scanStream, video, (result) => {
        if (result) this._handleScanDetected(storeCode, result.getText());
      });
      if (startId !== this._scanStartId) {
        controls.stop();
        return;
      }
      this._scanControls = controls;
    } catch {
      if (startId === this._scanStartId) {
        this._scanMessage = 'Camera scanning is not available in this browser — enter the barcode manually below.';
      }
    }
  }

  private _handleScanDetected(storeCode: string, raw: string) {
    const value = (raw || '').trim();
    if (!value || this._scanCooldown > Date.now()) return;
    this._scanFoundAnything = true;
    this._scanCooldown = Date.now() + 1600;
    this._clearScanFallbackTimer();

    // An outlet QR encodes `${MALL_URL}/s/<storeCode>` — jump to that
    // outlet's scan page instead of treating it as an item barcode.
    const scannedStore = this._storeCodeFromScan(value);
    if (scannedStore) {
      this._stopScanCamera();
      this._navigate(`/s/${encodeURIComponent(scannedStore)}/scan`);
      return;
    }
    void this._resolveBarcode(storeCode, value);
  }

  private _storeCodeFromScan(value: string): string | null {
    if (!/^https?:\/\//i.test(value)) return null;
    try {
      const match = new URL(value).pathname.match(/^\/s\/([^/]+)\/?$/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  private _clearScanFallbackTimer() {
    if (this._scanFallbackTimer !== undefined) {
      window.clearTimeout(this._scanFallbackTimer);
      this._scanFallbackTimer = undefined;
    }
  }

  private _stopScanCamera() {
    this._scanStartId += 1;
    this._clearScanFallbackTimer();
    if (this._scanTimer !== undefined) {
      window.clearInterval(this._scanTimer);
      this._scanTimer = undefined;
    }
    if (this._scanControls) {
      try {
        this._scanControls.stop();
      } catch {
        /* controls already stopped */
      }
      this._scanControls = undefined;
    }
    if (this._scanStream) {
      this._scanStream.getTracks().forEach((t) => t.stop());
      this._scanStream = null;
    }
    this._scanCameraOn = false;
    this._scanFoundAnything = false;
  }

  private async _resolveBarcode(storeCode: string, barcode: string) {
    if (!barcode) return;
    this._scanMessage = 'Looking up…';
    try {
      const item = await api.scanResolve(storeCode, barcode);
      this._scanResult = item;
      this._scanMessage = '';
    } catch (err: any) {
      this._scanResult = null;
      this._scanMessage = 'Item not found for this barcode';
      void this._refreshOutlets(); // failure may mean the outlet just went offline
    }
  }

  private _addScanLine(item: MallItem) {
    if (item.purchasable === false) {
      this._scanMessage = 'This item must be paid for at the counter — please hand it to the cashier.';
      return;
    }
    const line = this._scanLines.find((l) => l.stockId === item.id);
    if (line) {
      line.quantity += 1;
      this._scanLines = [...this._scanLines];
    } else {
      this._scanLines = [...this._scanLines, {
        stockId: item.id, code: item.code, name: item.name, price: item.price, quantity: 1,
      }];
    }
    this._toastMsg(`${item.name} added`);
  }

  private async _sendToCounter(storeCode: string) {
    if (this._scanLines.length === 0) return;
    const invalid = this._validateShopper();
    if (invalid) {
      this._scanMessage = invalid;
      return;
    }
    try {
      const res = await api.instoreHold(
        storeCode,
        this._scanLines.map((l) => ({ stockId: l.stockId, quantity: l.quantity })),
        this._shopperPayload(),
      );
      this._holdInfo = res;
      this._scanLines = [];
      this._scanMessage = '';
      this._profileKey = ''; // the shopper record may have just been created
    } catch (err: any) {
      if (err.code === 'PROFILE_REQUIRED') {
        this._profile = { found: false, shopperType: '', name: '', companyName: '', address: '', phone: '' };
        this._profileKey = '';
      }
      this._scanMessage = err.message || 'Could not send the cart to the counter';
    }
  }

  private async _releaseHold(storeCode: string) {
    if (!this._holdInfo) return;
    try {
      await api.instoreRelease(this._holdInfo.holdId, storeCode);
    } catch {
      /* best effort */
    }
    this._holdInfo = null;
    this._toastMsg('Cart cancelled');
  }

  // ─────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────

  private _fmt(n: number | undefined): string {
    return (n ?? 0).toFixed(2);
  }

  private _skeletons() {
    return html`<div class="grid">${[0, 1, 2, 3, 4, 5].map(() => html`
      <div class="card skeleton"><div class="thumb"></div><div class="info"><div class="line"></div><div class="line" style="width:60%"></div></div></div>`)}</div>`;
  }

  /** One outlet row — used by discovery results and the saved lists. The star
   *  toggles this device's favourite list. */
  private _renderOutletRow(o: OutletRowData, compact = false) {
    const fav = isFavouriteOutlet(o.code);
    const offline = o.online === false;
    const meta = [
      offline ? 'Offline' : (o.itemCount !== undefined ? `${o.itemCount} item(s)` : ''),
      this._outletDistanceLabel(o),
    ].filter(Boolean).join(' · ');
    const open = () => this._openOutlet(o);
    return html`
      <div class="outlet-row ${compact ? 'compact' : ''} ${offline ? 'offline' : ''}"
        role="button" tabindex="0"
        @click=${open}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
        <span class="pin">🏪</span>
        <span class="grow">
          <span class="title">${o.name}</span>
          ${!compact && o.location ? html`<span class="muted">${o.location}</span>` : nothing}
          ${meta ? html`<span class="chipsline">${meta}</span>` : nothing}
        </span>
        <button class="fav ${fav ? 'on' : ''}"
          title=${fav ? 'Remove from favourites' : 'Save to favourites'}
          aria-label=${fav ? `Remove ${o.name} from favourites` : `Add ${o.name} to favourites`}
          @click=${(e: Event) => { e.stopPropagation(); this._toggleFavourite({ code: o.code, name: o.name }); }}>
          ${fav ? '★' : '☆'}
        </button>
        <span class="chev">›</span>
      </div>`;
  }

  /** Favourite outlets + recently viewed outlets (nothing when both are empty).
   *  `maxRows` keeps Home compact; 0 means "show all". */
  private _renderSavedOutlets(maxRows = 0) {
    const favourites = this._favOutlets
      .slice(0, maxRows || undefined)
      .map((f) => this._mergeSavedOutlet(f));
    // Recents are shown even when the outlet is also a favourite — otherwise
    // starring the only outlet you browsed would make "Recently viewed"
    // disappear entirely. The row's filled star still shows the saved state.
    const recents = this._recentOutlets
      .slice(0, maxRows || undefined)
      .map((r) => this._mergeSavedOutlet(r));
    if (favourites.length === 0 && recents.length === 0) return nothing;
    return html`
      ${favourites.length > 0 ? html`
        <div class="saved-block">
          <div class="saved-head">
            <div class="section-title">⭐ Favourite outlets</div>
          </div>
          <div class="outlet-list">${favourites.map((o) => this._renderOutletRow(o, true))}</div>
        </div>` : nothing}
      ${recents.length > 0 ? html`
        <div class="saved-block">
          <div class="saved-head">
            <div class="section-title">🕘 Recently viewed</div>
            <button class="link" @click=${() => clearRecentOutlets()}>Clear</button>
          </div>
          <div class="outlet-list">${recents.map((o) => this._renderOutletRow(o, true))}</div>
        </div>` : nothing}`;
  }

  private _card(item: MallItem) {
    const img = item.thumbnailUrl || item.imageUrl;
    const offline = item.storeOnline === false || this._isOutletOffline(item.storeCode);
    // While offline the QOH is a stale snapshot (the gateway blanks it), so
    // `purchasable` decides. Items of an offline outlet remain addable.
    const canAdd = offline ? item.purchasable !== false : item.qoh > 0;
    return html`
      <div class="card" @click=${() => this._navigate(`/p/${encodeURIComponent(item.storeCode)}/${encodeURIComponent(item.id)}`)}>
        <div class="thumb">${img
          ? html`<img src=${img} loading="lazy" alt="" @error=${(e: Event) => this._imgFallback(e)}>`
          : html`${ITEM_PLACEHOLDER}`}</div>
        <div class="info">
          <div class="name">${item.name}</div>
          <div class="price">RM ${this._fmt(item.price)}</div>
          <div class="store">${item.storeName || item.storeCode}</div>
          ${offline
            ? html`<div class="chipsline">Offline · last synced</div>`
            : (item.qoh <= 0 ? html`<div class="oos">Out of stock</div>` : nothing)}
        </div>
        ${canAdd ? html`<button class="add" aria-label="Add to cart" @click=${(e: Event) => { e.stopPropagation(); this._addItem(item); }}>+</button>` : nothing}
      </div>`;
  }

  /** Replace a broken/offline image with the item placeholder (mall images are
   *  streamed through the outlet's tunnel, so they are unavailable offline). */
  private _imgFallback(e: Event) {
    const img = e.target as HTMLImageElement;
    const holder = img.parentElement;
    img.remove();
    if (holder && !holder.querySelector('.img-ph')) {
      const span = document.createElement('span');
      span.className = 'img-ph';
      span.textContent = ITEM_PLACEHOLDER;
      holder.appendChild(span);
    }
  }

  private _statusLabel(status: string): string {
    switch (status) {
      case 'pending_confirmation': return 'Waiting for the outlet';
      case 'available': return 'Ready — confirm your order';
      case 'unavailable': return 'Items no longer available';
      case 'queued': return 'Queued — sending to the outlet';
      case 'placed': return 'Order placed';
      case 'pending': return 'Payment pending';
      case 'awaiting_payment': return 'Awaiting payment';
      case 'paid': return 'Paid';
      case 'ready': return 'Ready for pickup';
      case 'recalled': return 'Being processed at the counter';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      case 'rejected': return 'Could not be accepted';
      case 'expired': return 'Expired';
      default: return status;
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Pages
  // ─────────────────────────────────────────────────────────────────────

  private _renderCategoryTiles() {
    // Only categories that actually have published items — an empty tile is
    // a dead end for shoppers. The system "Uncategorised" bucket follows the
    // same rule.
    const roots = rootCategories(this._categories).filter((c) => (c.itemCount ?? 0) > 0);
    if (!roots.length) return nothing;
    return html`
      <div class="section-title">Shop by category</div>
      <div class="cat-tiles" @wheel=${this._onRowWheel}>
        ${roots.map((c) => html`
          <button class="cat-tile" @click=${() => this._navigate(categoryHref(c))}>
            <span class="cat-icon">${categoryIcon(c)}</span>
            <span class="cat-name">${c.name}</span>
            ${c.itemCount !== undefined ? html`<span class="cat-count">${c.itemCount}</span>` : nothing}
          </button>`)}
      </div>`;
  }

  private _renderHome() {
    return html`
      <div class="banner">
        <h2>🛍️ ${APP_DISPLAY_NAME}</h2>
        <p>${RETAIL
          ? 'Shop from your neighbourhood store — pay online or at the counter when you collect.'
          : 'Shop from your neighbourhood pharmacy — pay online or at the counter when you collect.'}</p>
      </div>
      ${this._renderCategoryTiles()}
      ${this._renderSavedOutlets(4)}
      <div class="outlet-finder">
        <div class="finder-title">🏪 Find an outlet</div>
        <div class="finder-actions">
          <button class="btn primary" style="flex:1" @click=${() => this._navigate('/outlets?near=1')}>
            📍 Find outlets near me
          </button>
        </div>
        <div class="finder-sep">or search by name / area</div>
        <div class="finder-row">
          <input
            placeholder="Search outlet by name or area…"
            .value=${this._homeOutletQuery}
            @input=${(e: any) => { this._homeOutletQuery = e.target.value; }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                const q = this._homeOutletQuery.trim();
                if (q) this._navigate(`/outlets?q=${encodeURIComponent(q)}`);
              }
            }}>
          <button class="btn primary" ?disabled=${!this._homeOutletQuery.trim()} @click=${() => {
            const q = this._homeOutletQuery.trim();
            if (q) this._navigate(`/outlets?q=${encodeURIComponent(q)}`);
          }}>🔍</button>
        </div>
      </div>
      ${this._homeError ? html`<div class="error">${this._homeError}</div>` : nothing}
      ${this._homeLoading && this._homeItems.length === 0 ? this._skeletons() : nothing}
      <div class="grid">${this._homeItems.map((i) => this._card(i))}</div>
      ${this._homeLoading && this._homeItems.length > 0 ? html`<div class="empty">Loading…</div>` : nothing}
      ${!this._homeLoading && this._homeItems.length === 0 && !this._homeError
        ? (this._outlets.some((o) => (o.itemCount ?? 0) > 0)
            ? html`<div class="empty">The outlet is temporarily unreachable — retrying&hellip;</div>`
            : html`<div class="empty">No items are published yet. Check back soon!</div>`)
        : nothing}
      ${this._homeItems.length < this._homeTotal
        ? html`<div class="load-more"><button class="btn" @click=${() => this._loadHome(this._homePage + 1)}>Load more</button></div>`
        : nothing}
    `;
  }

  private _renderOutlets() {
    return html`
      <div class="page-head">
        <button class="back" @click=${() => this._navigate('/')}>←</button>
        <div>
          <div class="section-title" style="margin:0">🏪 Find ${RETAIL ? 'a shop' : 'a pharmacy'}</div>
          <div class="muted">
            ${this._outletSearchActive
              ? `${this._outletTotal} outlet(s)${this._outletNear ? ` within ${this._outletRadius} km` : ''}`
              : 'Search by name or area, or find outlets near you'}
          </div>
        </div>
      </div>

      <div class="outlet-finder">
        <div class="finder-row">
          <input
            placeholder="Search by outlet name or area…"
            .value=${this._outletQuery}
            @input=${(e: any) => this._onOutletQueryInput(e.target.value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                if (this._outletSearchTimer !== undefined) {
                  window.clearTimeout(this._outletSearchTimer);
                  this._outletSearchTimer = undefined;
                }
                void this._fetchOutlets(1, true);
              }
            }}>
          <button class="btn primary" @click=${() => {
            if (this._outletSearchTimer !== undefined) {
              window.clearTimeout(this._outletSearchTimer);
              this._outletSearchTimer = undefined;
            }
            void this._fetchOutlets(1, true);
          }}>🔍</button>
        </div>
        <div class="finder-actions">
          <button class=${this._outletNear ? 'btn primary' : 'btn'} @click=${() => void this._toggleNearMe()}>
            📍 ${this._outletNear ? 'Near me: on' : 'Find outlets near me'}
          </button>
          ${this._outletNear ? html`
            <select class="radius" @change=${(e: any) => this._onOutletRadiusChange(Number(e.target.value))}>
              ${[5, 10, 25, 50].map((r) => html`
                <option value=${r} ?selected=${r === this._outletRadius}>${r} km</option>`)}
            </select>` : nothing}
        </div>
      </div>

      ${this._outletGeoError ? html`<div class="error">${this._outletGeoError}</div>` : nothing}
      ${this._outletError ? html`<div class="error">${this._outletError}</div>` : nothing}
      ${!this._outletSearchActive
        ? html`
            ${this._renderSavedOutlets()}
            <div class="empty">
              Search by outlet name or area above, or tap <b>Find outlets near me</b>.
            </div>`
        : html`
            ${this._outletLoading && this._outletResults.length === 0
              ? html`<div class="empty">Searching outlets&hellip;</div>` : nothing}
            ${!this._outletLoading && this._outletResults.length === 0
              ? html`<div class="empty">
                  No outlet matches${this._outletQuery ? html` “${this._outletQuery}”` : nothing}${this._outletNear ? ' nearby — try a wider radius or search by name' : ''}.
                </div>` : nothing}
            <div class="outlet-list">
              ${this._outletResults.map((o) => this._renderOutletRow(o))}
            </div>
          `}

      ${this._outletResults.length < this._outletTotal
        ? html`<div class="load-more">
            <button class="btn" ?disabled=${this._outletLoading}
              @click=${() => void this._fetchOutlets(this._outletPage + 1, false)}>
              ${this._outletLoading ? 'Loading…' : 'Load more'}
            </button>
          </div>` : nothing}
    `;
  }

  private _renderCategoryStoreFilter(category: MallCategory | undefined) {
    const outlets = this._outlets.filter((o) => o.online !== false && (o.itemCount ?? 0) > 0);
    if (!outlets.length || !category) return nothing;
    return html`
      <div class="chips" @wheel=${this._onRowWheel}>
        <button class="${!this._catStore ? 'selected' : ''}"
          @click=${() => this._navigate(categoryHref(category))}>All outlets</button>
        ${outlets.map((o) => html`
          <button class="${this._catStore.toLowerCase() === o.code.toLowerCase() ? 'selected' : ''}"
            @click=${() => this._navigate(categoryHref(category, o.code))}>${o.name}</button>`)}
      </div>`;
  }

  private _renderCategory() {
    const category = findCategoryBySlug(this._categories, this._catSlug);
    const root = category?.parentId ? findCategoryById(this._categories, category.parentId) : category;
    const chips = root ? childrenOf(this._categories, root.id) : [];
    const activeId = category?.id || '';
    const title = category?.name || this._catSlug;
    const storeName = this._catStore
      ? (this._outlets.find((o) => o.code.toLowerCase() === this._catStore.toLowerCase())?.name || this._catStore)
      : '';

    return html`
      <div class="page-head">
        <button class="back" @click=${() => this._navigate('/')}>←</button>
        <div class="grow">
          <div class="section-title" style="margin:0">
            ${category ? categoryIcon(category) : '🛒'} ${title}
          </div>
          <div class="muted">
            ${root && category && root.id !== category.id
              ? html`<span style="cursor:pointer" @click=${() => this._navigate(categoryHref(root, this._catStore || undefined))}>${root.name}</span> · `
              : nothing}
            ${this._catTotal} item(s)${storeName ? ` at ${storeName}` : ' across all outlets'}
          </div>
        </div>
      </div>
      ${chips.length > 0 ? html`
        <div class="chips" @wheel=${this._onRowWheel}>
          <button class="${!activeId || activeId === root?.id ? 'selected' : ''}"
            @click=${() => root && this._navigate(categoryHref(root, this._catStore || undefined))}>
            All ${root?.name || ''}
          </button>
          ${chips.map((child) => html`
            <button class="${activeId === child.id ? 'selected' : ''}"
              @click=${() => this._navigate(categoryHref(child, this._catStore || undefined))}>
              ${child.name}
            </button>`)}
        </div>` : nothing}
      ${this._renderCategoryStoreFilter(category)}
      ${this._catError ? html`<div class="error">${this._catError}</div>` : nothing}
      ${this._catLoading && this._catItems.length === 0 ? this._skeletons() : nothing}
      ${!this._catLoading && !this._catError && this._catItems.length === 0
        ? html`<div class="empty">No items in this category yet. Browse another category or search the mall.</div>`
        : nothing}
      <div class="grid">${this._catItems.map((i) => this._card(i))}</div>
      ${this._catItems.length < this._catTotal
        ? html`<div class="load-more"><button class="btn" @click=${() => this._loadCategory(this._catSlug, this._catPage + 1)}>Load more</button></div>`
        : nothing}
    `;
  }

  private _renderSearch() {
    return html`
      <div class="section-title">Search results for "${this._queryText}"</div>
      ${this._searchLoading && this._searchItems.length === 0 ? html`<div class="empty">Searching…</div>` : nothing}
      ${!this._searchLoading && this._searchItems.length === 0
        ? html`<div class="empty">No items found.</div>` : nothing}
      <div class="grid">${this._searchItems.map((i) => this._card(i))}</div>
      ${this._searchItems.length < this._searchTotal
        ? html`<div class="load-more"><button class="btn" @click=${() => this._runSearch(this._queryText, this._searchPage + 1)}>Load more</button></div>`
        : nothing}
    `;
  }

  private _storeCategoryChips(storeCode: string) {
    const rootIds = new Set<string>();
    for (const item of this._storeItems) {
      for (const categoryId of item.categoryIds || []) {
        const category = findCategoryById(this._categories, categoryId);
        if (!category) continue;
        rootIds.add(category.parentId || category.id);
      }
    }
    const roots = rootCategories(this._categories).filter((c) => rootIds.has(c.id));
    if (!roots.length) return nothing;
    return html`
      <div class="chips" @wheel=${this._onRowWheel}>
        ${roots.map((c) => html`
          <button @click=${() => this._navigate(categoryHref(c, storeCode))}>
            ${categoryIcon(c)} ${c.name}
          </button>`)}
      </div>`;
  }

  private _renderStore(storeCode: string) {
    const outlet = this._outlets.find((o) => o.code.toLowerCase() === storeCode.toLowerCase());
    const offline = outlet?.online === false;
    const outletName = outlet?.name || storeCode;
    const fav = isFavouriteOutlet(storeCode);
    return html`
      <div class="page-head">
        <button class="back" @click=${() => this._navigate('/')}>←</button>
        <div class="grow">
          <div class="section-title" style="margin:0">🏪 ${outletName}</div>
          ${outlet?.location ? html`<div class="muted">📍 ${outlet.location}</div>` : nothing}
          <div class="muted">${offline ? 'Offline · showing last synced catalogue' : `${this._storeItems.length} item(s) available`}</div>
        </div>
        <button class="btn ${fav ? 'star-on' : ''}"
          title=${fav ? 'Remove from favourites' : 'Save to favourites'}
          @click=${() => this._toggleFavourite({ code: outlet?.code || storeCode, name: outletName })}>
          ${fav ? '★ Favourited' : '☆ Favourite'}
        </button>
      </div>
      ${offline ? html`
        <div class="offline-banner">
          ⚠️ This outlet is offline — showing the last synced catalogue. You can add items now;
          checkout opens when the outlet reconnects.
        </div>` : nothing}
      <button class="btn primary wide" style="margin-bottom:0.75rem"
        ?disabled=${offline}
        @click=${() => {
          if (offline) return;
          this._email
            ? this._navigate(`/s/${encodeURIComponent(storeCode)}/scan`)
            : this._openLogin(`/s/${encodeURIComponent(storeCode)}/scan`);
        }}>
        📷 Scan &amp; Go — scan items and pay at the counter
      </button>
      ${this._storeCategoryChips(storeCode)}
      ${this._storeLoading ? html`<div class="empty">Loading…</div>` : nothing}
      ${!this._storeLoading && this._storeItems.length === 0
        ? html`<div class="empty">This outlet has no published items right now.</div>` : nothing}
      <div class="grid">${this._storeItems.map((i) => this._card(i))}</div>
    `;
  }

  private _renderProductBreadcrumb(item: MallItem) {
    const categoryIds = item.categoryIds || [];
    if (!categoryIds.length) return nothing;
    const path = categoryPath(this._categories, categoryIds[0]);
    if (!path.length) return nothing;
    return html`
      <div class="muted" style="margin:0.1rem 0 0.4rem">
        <span style="cursor:pointer" @click=${() => this._navigate('/')}>Home</span>
        ${path.map((c) => html`
          › <span style="cursor:pointer" @click=${() => this._navigate(categoryHref(c))}>${c.name}</span>`)}
      </div>`;
  }

  private _renderProduct() {
    const p = this._product;
    if (this._productLoading) return html`<div class="empty">Loading…</div>`;
    if (!p) return html`<div class="empty">Item not available.</div>`;
    const images = (p.imageUrls && p.imageUrls.length > 0) ? p.imageUrls : (p.imageUrl ? [p.imageUrl] : []);
    const offline = p.storeOnline === false
      || (p.storeCode ? this._isOutletOffline(p.storeCode) : false);
    return html`
      ${offline ? html`
        <div class="offline-banner">
          ⚠️ This outlet is offline — showing the last synced catalogue. You can add this item now;
          checkout opens when the outlet reconnects.
        </div>` : nothing}
      <div class="page-head">
        <button class="back" @click=${() => window.history.back()}>←</button>
        <div class="muted" style="cursor:pointer" @click=${() => this._navigate(`/s/${encodeURIComponent(p.storeCode)}`)}>
          🏪 ${p.storeName || p.storeCode}
        </div>
      </div>
      ${this._renderProductBreadcrumb(p)}
      ${images.length > 0 ? html`
        <div class="carousel">
          ${images.map((src) => html`<div class="cand"><img src=${src} alt="" @error=${(e: Event) => this._imgFallback(e)}></div>`)}
        </div>` : nothing}
      <div class="detail" style="margin-top:0.6rem">
        <div style="display:flex;justify-content:space-between;gap:0.5rem">
          <div>
            <div style="font-weight:600">${p.name}</div>
            <div class="muted">${p.code}${p.typeName ? ` · ${p.typeName}` : ''}</div>
          </div>
          <div class="price">RM ${this._fmt(p.price)}</div>
        </div>
        <div class="muted" style="margin:0.4rem 0">
          ${offline
            ? 'Availability confirmed when the shop reopens'
            : (p.qoh > 0 ? `In stock${p.qohString ? ` (${p.qohString})` : ''}` : 'Out of stock')}
          ${p.listingEnd ? ` · Offer until ${new Date(p.listingEnd * 1000).toLocaleDateString()}` : ''}
        </div>
        ${p.description ? html`<div style="font-size:0.86rem">${p.description}</div>` : nothing}
        <div style="display:flex;align-items:center;gap:0.75rem;margin-top:0.8rem">
          <div class="qty">
            <button @click=${() => { this._productQty = Math.max(1, this._productQty - 1); }}>−</button>
            <span>${this._productQty}</span>
            <button @click=${() => { this._productQty = this._productQty + 1; }}>＋</button>
          </div>
          <button class="btn primary" style="flex:1"
            ?disabled=${!offline && p.qoh <= 0}
            @click=${() => this._addItem(p, this._productQty)}>
            ${!offline && p.qoh <= 0 ? 'Out of stock' : 'Add to cart'}
          </button>
        </div>
      </div>
    `;
  }

  private _renderCart() {
    const cart = this._cart;
    if (!cart || cart.items.length === 0) {
      return html`<div class="empty">🛒 Your cart is empty.<br><br>
        <button class="btn primary" @click=${() => this._navigate('/')}>Browse the mall</button></div>`;
    }
    return html`
      <div class="section-title">🛒 Cart — ${cart.storeName || cart.storeCode}</div>
      <div class="list">
        ${cart.items.map((i) => html`
          <div class="row">
            ${i.imageUrl ? html`<img src=${i.imageUrl} alt="">` : html`<div style="width:52px;height:52px;border-radius:8px;background:#f0f2f5;display:flex;align-items:center;justify-content:center">${ITEM_PLACEHOLDER}</div>`}
            <div class="grow">
              <div class="title">${i.name}</div>
              <div class="muted">RM ${this._fmt(i.price)}</div>
            </div>
            <div class="qty">
              <button @click=${() => { updateQuantity(i.stockId, i.quantity - 1); this._cart = loadCart(); }}>−</button>
              <span>${i.quantity}</span>
              <button @click=${() => { updateQuantity(i.stockId, i.quantity + 1); this._cart = loadCart(); }}>＋</button>
            </div>
            <button class="back" title="Remove" @click=${() => { removeItem(i.stockId); this._cart = loadCart(); }}>🗑️</button>
          </div>`)}
      </div>
      <div class="detail" style="margin-top:0.9rem">
        <div style="display:flex;justify-content:space-between">
          <span>Subtotal</span><b>RM ${this._fmt(this._cartSubtotal())}</b>
        </div>
        <div class="muted" style="margin:0.4rem 0">Pick up at ${cart.storeName || cart.storeCode}. One outlet per cart — pay online or at the counter on collection.</div>
        <button class="btn primary wide" @click=${() => this._navigate(this._email ? '/checkout' : '/checkout')}>
          Checkout
        </button>
      </div>
    `;
  }

  /**
   * Shopper identity block shared by checkout and scan & go.
   * Known email → read-only "who this order is for"; unknown email → the
   * Member / Wholesale customer choice plus name, company, address, phone.
   */
  private _renderShopperFields() {
    if (this._profileLoading) {
      return html`<div class="muted" style="margin-top:0.8rem">Checking your account…</div>`;
    }
    if (this._profileKnown()) {
      const isCustomer = this._shopperType === 'customer';
      const who = isCustomer
        ? (this._checkoutCompany || this._checkoutName || this._email)
        : (this._checkoutName || this._email);
      return html`
        <div class="detail" style="margin-top:0.9rem">
          <div class="muted">This order is for</div>
          <div style="font-weight:600">${isCustomer ? '🏢 Wholesale customer' : '👤 Member'} · ${who}</div>
          <div class="muted" style="margin-top:0.25rem">
            Saved at the counter as a <b>${isCustomer ? 'Hold Wholesales' : 'Hold Sales'}</b> —
            the staff recalls it to take payment.
          </div>
        </div>`;
    }
    const isCustomer = this._shopperType === 'customer';
    // Existing member/customer whose record lacks a phone (or company): keep
    // the identity fixed, but let the shopper supply the missing contact
    // details instead of blocking checkout with no field to fix.
    if (this._profile?.found) {
      return html`
        <div class="detail" style="margin-top:0.9rem">
          <div class="muted">This order is for</div>
          <div style="font-weight:600">${isCustomer ? '🏢 Wholesale customer' : '👤 Member'}</div>
          <div class="muted" style="margin-top:0.25rem">
            Saved at the counter as a <b>${isCustomer ? 'Hold Wholesales' : 'Hold Sales'}</b> —
            please complete your details.
          </div>
        </div>
        <div class="form-group">
          <label>${isCustomer ? 'Contact name' : 'Your name'}</label>
          <input .value=${this._checkoutName} @input=${(e: any) => { this._checkoutName = e.target.value; }}>
        </div>
        ${isCustomer ? html`
          <div class="form-group">
            <label>Company name</label>
            <input .value=${this._checkoutCompany} @input=${(e: any) => { this._checkoutCompany = e.target.value; }}>
          </div>` : nothing}
        <div class="form-group">
          <label>Address</label>
          <input .value=${this._checkoutAddress} @input=${(e: any) => { this._checkoutAddress = e.target.value; }}>
        </div>
        <div class="form-group">
          <label>Telephone number</label>
          <input type="tel" .value=${this._checkoutPhone} @input=${(e: any) => { this._checkoutPhone = e.target.value; }}>
        </div>`;
    }
    return html`
      <div class="form-group" style="margin-top:0.9rem">
        <label>I am a</label>
        <div class="pay-choice">
          <label>
            <input type="radio" name="shopper" value="member" .checked=${!isCustomer}
              @change=${() => { this._shopperType = 'member'; }}>
            <span><b>Member</b><br><span class="muted">Personal purchase — saved as Hold Sales</span></span>
          </label>
          <label>
            <input type="radio" name="shopper" value="customer" .checked=${isCustomer}
              @change=${() => { this._shopperType = 'customer'; }}>
            <span><b>Wholesale customer</b><br><span class="muted">Business purchase — saved as Hold Wholesales</span></span>
          </label>
        </div>
      </div>
      <div class="form-group">
        <label>${isCustomer ? 'Contact name' : 'Your name'}</label>
        <input .value=${this._checkoutName} @input=${(e: any) => { this._checkoutName = e.target.value; }}>
      </div>
      ${isCustomer ? html`
        <div class="form-group">
          <label>Company name</label>
          <input .value=${this._checkoutCompany} @input=${(e: any) => { this._checkoutCompany = e.target.value; }}>
        </div>` : nothing}
      <div class="form-group">
        <label>Address</label>
        <input .value=${this._checkoutAddress} @input=${(e: any) => { this._checkoutAddress = e.target.value; }}>
      </div>
      <div class="form-group">
        <label>Telephone number</label>
        <input type="tel" .value=${this._checkoutPhone} @input=${(e: any) => { this._checkoutPhone = e.target.value; }}>
      </div>`;
  }

  private _renderCheckout() {
    const cart = this._cart;
    if (!cart || cart.items.length === 0) {
      return html`<div class="empty">Your cart is empty. <button class="btn" @click=${() => this._navigate('/')}>Browse</button></div>`;
    }
    if (!this._email) {
      return html`<div class="empty">
        <p>Sign in to place your order.</p>
        <button class="btn primary" @click=${() => this._openLogin('/checkout')}>Sign in</button>
      </div>`;
    }
    const offline = this._isOutletOffline(cart.storeCode);
    return html`
      <div class="section-title">Checkout — ${cart.storeName || cart.storeCode}</div>
      ${offline ? html`
        <div class="offline-banner">
          ⚠️ This outlet is offline right now. You can still place the order — we save it and confirm it
          as soon as the outlet reconnects.
        </div>` : nothing}
      <div class="detail">
        ${cart.items.map((i) => html`
          <div style="display:flex;justify-content:space-between;font-size:0.88rem;margin-bottom:0.3rem">
            <span>${i.name} × ${i.quantity}</span><span>RM ${this._fmt(i.price * i.quantity)}</span>
          </div>`)}
        <div style="display:flex;justify-content:space-between;border-top:1px solid var(--color-border);padding-top:0.45rem;margin-top:0.45rem">
          <b>Total</b><b>RM ${this._fmt(this._cartSubtotal())}</b>
        </div>
        ${this._checkoutPayment === 'online' && this._paymentFeeMode === 'customer' && this._paymentFee > 0 ? html`
          <div style="display:flex;justify-content:space-between;font-size:0.85rem;margin-top:0.25rem">
            <span>Online payment fee</span><span>RM ${this._fmt(this._paymentFee)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;font-weight:600;margin-top:0.2rem">
            <span>Pay now</span><span>RM ${this._fmt(this._paymentCustomerTotal)}</span>
          </div>` : nothing}

        ${this._renderShopperFields()}

        <div class="form-group">
          <label>Payment</label>
          <div class="pay-choice">
            <label>
              <input type="radio" name="pay" value="counter" .checked=${this._checkoutPayment === 'counter'}
                @change=${() => { this._checkoutPayment = 'counter'; }}>
              <span><b>Pay at the counter</b><br><span class="muted">Order now, collect and pay at the outlet</span></span>
            </label>
            ${this._onlinePayment ? html`
              <label>
                <input type="radio" name="pay" value="online" .checked=${this._checkoutPayment === 'online'}
                  @change=${() => { this._checkoutPayment = 'online'; }}>
                <span><b>Pay online (FPX / card)</b><br><span class="muted">Pay now via ${this._paymentProviderLabel || 'the outlet\'s payment gateway'}; collect later</span></span>
              </label>` : nothing}
          </div>
          ${!this._onlinePayment ? html`
            <div class="muted" style="margin-top:0.35rem">
              ${this._onlinePaymentNote || 'Online payment is not available at this outlet'} — pay at the counter on collection.
            </div>` : nothing}
        </div>

        ${this._checkoutError ? html`<div class="error">${this._checkoutError}</div>` : nothing}
        <button class="btn primary wide" ?disabled=${this._checkoutBusy} @click=${() => this._submitCheckout()}>
          ${this._checkoutBusy ? 'Placing order…' : 'Place order'}
        </button>
        <div class="muted" style="margin-top:0.5rem">Orders are prepared by the outlet. Counter-payment orders are held until the next day.</div>
      </div>
    `;
  }

  /**
   * The gateway can only reach a shopper who registered a device token, so the
   * prompt is shown exactly where it matters: next to an order that is waiting
   * for the outlet to come back.
   */
  private _renderNotifyBanner() {
    if (!pushConfigured() || this._pushPermission === 'granted') return nothing;
    const waiting = this._orders.some(
      (o) => o.status === 'pending_confirmation' || o.status === 'available' || o.status === 'queued');
    if (!waiting) return nothing;
    if (this._pushPermission === 'denied' || this._pushPermission === 'unsupported') {
      return html`
        <div class="offline-banner">
          🔕 Notifications are blocked for this site — allow them in your browser settings and we
          can tell you the moment this order is ready.
        </div>`;
    }
    return html`
      <div class="offline-banner" style="background:#eef6ff;border-color:var(--color-primary)">
        <div>🔔 Get told when this order is ready to confirm.</div>
        <button class="btn primary" style="margin-top:0.4rem" ?disabled=${this._pushBusy}
          @click=${() => this._enableNotifications()}>
          ${this._pushBusy ? 'Enabling…' : 'Notify me'}
        </button>
      </div>`;
  }

  private async _enableNotifications() {
    this._pushBusy = true;
    try {
      const result = await enablePush();
      this._pushPermission = pushPermission();
      if (result === 'granted') {
        this._toastMsg('Notifications on — we will tell you when your order is ready.');
      } else if (result === 'denied') {
        this._toastMsg('Notifications are blocked in your browser settings.');
      } else if (result === 'skipped' || result === 'unsupported') {
        this._toastMsg('This deployment cannot send notifications yet.');
      } else if (result === 'error') {
        this._toastMsg('Could not enable notifications — please try again.');
      }
    } finally {
      this._pushBusy = false;
    }
  }

  private _renderOrders() {
    if (!this._email) {
      return html`<div class="empty">
        <p>Sign in to see your orders.</p>
        <button class="btn primary" @click=${() => this._openLogin('/orders')}>Sign in</button>
      </div>`;
    }
    return html`
      <div class="page-head">
        <div class="section-title" style="margin:0;flex:1">📦 My Orders</div>
        <button class="btn" ?disabled=${this._ordersLoading} @click=${() => this._loadOrders()}>↻</button>
      </div>
      ${this._ordersError ? html`<div class="error">${this._ordersError}</div>` : nothing}
      ${this._renderNotifyBanner()}
      ${this._orders.length === 0 && !this._ordersLoading ? html`<div class="empty">No orders yet.</div>` : nothing}
      <div class="list">
        ${this._orders.map((o) => html`
          <div class="row" style="flex-direction:column;align-items:stretch">
            <div style="display:flex;gap:0.5rem;align-items:center">
              <div class="grow">
                <div class="title">${o.refNo || o.id.slice(0, 8)}
                  <span class="status ${o.status}">${this._statusLabel(o.status)}</span>
                </div>
                <div class="muted">${o.storeCode} · RM ${this._fmt(o.total)} ·
                  ${o.paymentMode === 'online'
                    ? `${o.paymentProvider === 'billplz' ? 'Billplz' : (o.paymentProvider === 'toyyibpay' ? 'ToyyibPay' : 'Online')} · ${o.paymentStatus || 'pending'}`
                    : 'Pay at counter'}</div>
                ${o.kind === 'hold' ? html`
                  <div class="muted" style="font-size:0.78rem">
                    ${o.status === 'pending_confirmation'
                      ? '📥 Saved — we will notify you when the outlet is back and the items are available'
                      : o.status === 'queued'
                        ? '📤 Sending to the outlet — this is retried automatically'
                        : o.status === 'pending'
                          ? '⏳ Waiting for payment confirmation — your sale is saved automatically when the gateway approves it'
                          : o.status === 'recalled'
                            ? '🧾 Recalled at the counter — the cashier is closing your order'
                            : o.status === 'completed'
                              ? '✅ Completed — sale saved at the outlet'
                              : `${o.shopperType === 'customer' ? '🏢 Hold Wholesales' : '👤 Hold Sales'} at the counter`}
                  </div>` : nothing}
                ${o.message ? html`
                  <div class="muted" style="font-size:0.78rem">ℹ️ ${o.message}</div>` : nothing}
                ${(o.status === 'available' || o.status === 'pending_confirmation') ? html`
                  <div class="confirm-panel">
                    <div class="muted" style="font-size:0.8rem">
                      Complete your order — the outlet checks stock when you confirm.
                    </div>
                    <div class="confirm-actions">
                      <button class="btn primary" ?disabled=${this._confirmBusy === o.id}
                        @click=${() => this._confirmOrder(o.id, 'counter')}>
                        ${this._confirmBusy === o.id ? 'Confirming…' : 'Confirm · pay at counter'}
                      </button>
                      ${this._onlinePayment ? html`
                        <button class="btn" ?disabled=${this._confirmBusy === o.id}
                          @click=${() => this._confirmOrder(o.id, 'online')}>
                          Confirm · pay online
                        </button>` : nothing}
                    </div>
                  </div>` : nothing}
              </div>
              <button class="back" @click=${() => { this._expandedOrder = this._expandedOrder === o.id ? '' : o.id; }}>
                ${this._expandedOrder === o.id ? '▲' : '▼'}
              </button>
            </div>
            ${this._expandedOrder === o.id ? html`
              <div style="margin-top:0.5rem">
                ${o.items.map((it) => html`
                  <div style="display:flex;justify-content:space-between;font-size:0.82rem">
                    <span>${it.name || it.code} × ${it.quantity}</span>
                    <span>RM ${this._fmt(it.price * it.quantity)}</span>
                  </div>`)}
                <div style="display:flex;gap:0.5rem;margin-top:0.6rem;flex-wrap:wrap">
                  ${(o.status === 'pending' || o.status === 'awaiting_payment') ? html`
                    <button class="btn" @click=${async () => { try { await api.requeryOrder(o.id); await this._loadOrders(); } catch { /* ignore */ } }}>
                      Check payment
                    </button>` : nothing}
                  ${(o.status === 'placed' || o.status === 'pending' || o.status === 'awaiting_payment') ? html`
                    <button class="btn danger" @click=${async () => {
                      if (!window.confirm('Cancel this order?')) return;
                      try { await api.cancelOrder(o.id); await this._loadOrders(); this._toastMsg('Order cancelled'); }
                      catch (err: any) { this._toastMsg(err.message || 'Could not cancel'); }
                    }}>Cancel</button>` : nothing}
                </div>
              </div>` : nothing}
          </div>`)}
      </div>
    `;
  }

  private _renderPaymentReturn() {
    const provider = paymentProviderLabel(this._payReturnProvider);
    const refNo = this._payReturnRefNo || this._payReturnOrderId.slice(0, 8);
    const waiting = this._payReturnStatus === 'verifying';
    return html`
      <div class="empty" style="padding-top:3rem">
        ${waiting ? html`
          <div style="font-size:2rem">⏳</div>
          <p><b>Waiting for ${provider} to verify your payment…</b></p>
          <p class="muted">Order ${refNo} — this page checks automatically, please keep it open.</p>` : nothing}
        ${this._payReturnStatus === 'paid' ? html`
          <div style="font-size:2rem">✅</div>
          <p><b>Payment verified!</b></p>
          <p>Taking you to your orders…</p>` : nothing}
        ${this._payReturnStatus === 'unconfirmed' ? html`
          <div style="font-size:2rem">ℹ️</div>
          <p><b>We haven't received ${provider}'s confirmation yet.</b></p>
          <p class="muted">${this._payReturnDetail
            || 'If you completed the payment it can take a minute. Check again, or cancel the order.'}</p>` : nothing}
        ${this._payReturnStatus === 'cancelled' ? html`
          <div style="font-size:2rem">🚫</div>
          <p><b>${this._payReturnDetail || 'This order was cancelled.'}</b></p>
          <p class="muted">Nothing will be charged. Order ${refNo}</p>` : nothing}
        ${this._payReturnStatus === 'signin' ? html`
          <div style="font-size:2rem">🔒</div>
          <p><b>Sign in to confirm this payment.</b></p>
          <p class="muted">Order ${refNo}</p>` : nothing}
        ${this._payReturnStatus === 'error' ? html`
          <div style="font-size:2rem">⚠️</div>
          <p>${this._payReturnDetail || 'This payment link is not valid.'}</p>` : nothing}
        <div style="margin-top:1rem;display:flex;gap:0.5rem;justify-content:center;flex-wrap:wrap">
          ${this._payReturnStatus === 'signin' ? html`
            <button class="btn primary" @click=${() => this._openLogin(this._path)}>Sign in</button>` : nothing}
          ${waiting ? html`
            <button class="btn primary" disabled aria-disabled="true">Return to My Orders</button>` : nothing}
          ${this._payReturnStatus === 'paid' || this._payReturnStatus === 'cancelled' ? html`
            <button class="btn primary" @click=${() => this._navigate('/orders')}>Return to My Orders</button>` : nothing}
          ${this._payReturnStatus === 'unconfirmed' ? html`
            ${this._payReturnCancelable ? html`
              <button class="btn danger" ?disabled=${this._payReturnCancelBusy}
                @click=${() => this._cancelPendingOrder()}>
                ${this._payReturnCancelBusy ? 'Cancelling…' : 'Cancel Order'}
              </button>` : nothing}
            <button class="btn" @click=${() => this._startPaymentReturn()}>Manual Check</button>` : nothing}
        </div>
      </div>
    `;
  }

  private _renderScan(storeCode: string) {
    if (!this._email) {
      return html`<div class="empty">
        <p>Sign in to use in-store scan &amp; go.</p>
        <button class="btn primary" @click=${() => this._openLogin(`/s/${encodeURIComponent(storeCode)}/scan`)}>Sign in</button>
      </div>`;
    }
    const outlet = this._outlets.find((o) => o.code === storeCode);
    return html`
      <div class="page-head">
        <button class="back" @click=${() => this._navigate(`/s/${encodeURIComponent(storeCode)}`)}>←</button>
        <div>
          <div class="section-title" style="margin:0">📷 Scan &amp; Go</div>
          <div class="muted">${outlet?.name || storeCode} — scan items, then pay at the counter</div>
        </div>
      </div>

      ${this._holdInfo ? html`
        <div class="hold-box">
          <b>✅ Cart sent to the counter!</b>
          <p style="margin:0.4rem 0">Show this at the counter to pay:</p>
          <div style="font-size:1.1rem;font-weight:700;margin:0.3rem 0">Ref: ${this._holdInfo.holdId.slice(0, 10)}</div>
          <div>Total: <b>RM ${this._fmt(this._holdInfo.total)}</b></div>
          <div style="display:flex;gap:0.5rem;margin-top:0.6rem">
            <button class="btn" @click=${() => this._releaseHold(storeCode)}>Cancel cart</button>
            <button class="btn primary" @click=${() => { this._holdInfo = null; }}>Scan more</button>
          </div>
        </div>` : nothing}

      ${!this._holdInfo ? html`
        ${this._scanCameraOn ? html`
          <video id="scan-video" class="scan" playsinline muted autoplay></video>
          <div class="muted" style="margin-top:0.4rem">Hold the barcode or QR code steady inside the frame.</div>` : html`
          <button class="btn primary wide" @click=${() => this._startScanCamera(storeCode)}>📷 Start camera</button>`}
        <div style="display:flex;gap:0.5rem;margin-top:0.5rem">
          <input style="flex:1;padding:0.5rem;border:1px solid var(--color-border);border-radius:8px"
            placeholder="Barcode (manual entry)" .value=${this._scanManual}
            @input=${(e: any) => { this._scanManual = e.target.value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._resolveBarcode(storeCode, this._scanManual.trim()); }}>
          <button class="btn" @click=${() => this._resolveBarcode(storeCode, this._scanManual.trim())}>Find</button>
        </div>
        ${this._scanMessage ? html`<div class="muted" style="margin-top:0.5rem">${this._scanMessage}</div>` : nothing}

        ${this._scanResult ? html`
          <div class="detail" style="margin-top:0.6rem">
            <div style="display:flex;justify-content:space-between;gap:0.5rem">
              <div>
                <b>${this._scanResult.name}</b>
                <div class="muted">${this._scanResult.code}</div>
              </div>
              <b>RM ${this._fmt(this._scanResult.price)}</b>
            </div>
            ${this._scanResult.purchasable === false
              ? html`<div class="error">Not available for self-checkout — hand it to the cashier.</div>`
              : html`<button class="btn primary wide" style="margin-top:0.5rem" @click=${() => this._addScanLine(this._scanResult!)}>Add</button>`}
          </div>` : nothing}

        <div class="section-title" style="margin-top:1rem">In-store cart (${this._scanLines.reduce((n, l) => n + l.quantity, 0)})</div>
        ${this._scanLines.length === 0 ? html`<div class="muted">Scan shelf items to add them.</div>` : html`
          <div class="list">
            ${this._scanLines.map((l) => html`
              <div class="row">
                <div class="grow">
                  <div class="title">${l.name}</div>
                  <div class="muted">RM ${this._fmt(l.price)} × ${l.quantity}</div>
                </div>
                <div class="qty">
                  <button @click=${() => { const line = this._scanLines.find((x) => x.stockId === l.stockId)!; line.quantity = Math.max(1, line.quantity - 1); this._scanLines = [...this._scanLines]; }}>−</button>
                  <button @click=${() => { const line = this._scanLines.find((x) => x.stockId === l.stockId)!; line.quantity += 1; this._scanLines = [...this._scanLines]; }}>＋</button>
                </div>
                <button class="back" @click=${() => { this._scanLines = this._scanLines.filter((x) => x.stockId !== l.stockId); }}>🗑️</button>
              </div>`)}
          </div>
          <div class="detail" style="margin-top:0.8rem">
            <div style="display:flex;justify-content:space-between"><b>Total</b><b>RM ${this._fmt(this._scanTotal())}</b></div>
            ${this._renderShopperFields()}
            <button class="btn primary wide" style="margin-top:0.6rem" @click=${() => this._sendToCounter(storeCode)}>
              🧾 Send cart to the counter
            </button>
            <div class="muted" style="margin-top:0.4rem">The cashier will recall your cart and take payment at the counter.</div>
          </div>`}
      ` : nothing}
    `;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────────────────────────────

  render() {
    const seg = this._path.split('/').filter(Boolean);
    let page = this._renderHome();
    if (seg[0] === 'search') page = this._renderSearch();
    else if (seg[0] === 'c' && seg[1]) page = this._renderCategory();
    else if (seg[0] === 'outlets') page = this._renderOutlets();
    else if (seg[0] === 's' && seg[1] && seg[2] === 'scan') page = this._renderScan(seg[1]);
    else if (seg[0] === 's' && seg[1]) page = this._renderStore(seg[1]);
    else if (seg[0] === 'p' && seg[1] && seg[2]) page = this._renderProduct();
    else if (seg[0] === 'cart') page = this._renderCart();
    else if (seg[0] === 'checkout') page = this._renderCheckout();
    else if (seg[0] === 'orders') page = this._renderOrders();
    else if (seg[0] === 'payment' && seg[1] === 'return') page = this._renderPaymentReturn();

    const cartBadge = cartCount(this._cart);
    return html`
      <div class="shell">
        <header>
          <div class="logo" @click=${() => this._navigate('/')}>🛍️ ${siteBrand()}${DEMO_SUFFIX}</div>
          <div class="search">
            <input placeholder="Search products…" .value=${this._searchInput}
              @input=${(e: any) => { this._searchInput = e.target.value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._navigate(`/search?q=${encodeURIComponent(this._searchInput.trim())}`); }}>
            <button @click=${() => this._navigate(`/search?q=${encodeURIComponent(this._searchInput.trim())}`)}>🔍</button>
          </div>
          <button class="account" @click=${() => (this._email ? this._navigate('/orders') : this._openLogin('/'))}>
            ${this._email ? '👤' : 'Sign in'}
          </button>
        </header>

        <main>${page}</main>

        <nav class="bottom">
          <button class="${seg[0] === undefined ? 'active' : ''}" @click=${() => this._navigate('/')}>
            <span class="icon">🏠</span>Home
          </button>
          <button class="${seg[0] === 'outlets' ? 'active' : ''}" @click=${() => this._navigate('/outlets')}>
            <span class="icon">🏪</span>Outlets
          </button>
          <button class="${seg[0] === 'cart' ? 'active' : ''}" @click=${() => this._navigate('/cart')}>
            <span class="icon badge" data-count=${cartBadge > 0 ? String(cartBadge) : ''}>🛒</span>Cart
          </button>
          <button class="${seg[0] === 'orders' ? 'active' : ''}" @click=${() => (this._email ? this._navigate('/orders') : this._openLogin('/orders'))}>
            <span class="icon">📦</span>Orders
          </button>
          <button @click=${() => (this._email ? this._doLogout() : this._openLogin('/'))}>
            <span class="icon">${this._email ? '🚪' : '👤'}</span>${this._email ? 'Sign out' : 'Account'}
          </button>
        </nav>

        ${this._toast ? html`<div class="toast">${this._toast}</div>` : nothing}

        ${this._loginOpen ? html`
          <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._loginOpen = false; }}>
            <div class="modal">
              <h3>Sign in</h3>
              ${this._loginError ? html`<div class="error">${this._loginError}</div>` : nothing}
              <button class="btn wide" ?disabled=${this._loginBusy} @click=${() => this._doGoogleLogin()}>
                Continue with Google
              </button>
              <div class="muted" style="margin-top:0.8rem;text-align:center">or sign in with email link</div>
              <div class="form-group" style="margin-top:0.6rem">
                <label>Email</label>
                <input type="email" placeholder="you@example.com" .value=${this._loginEmail} ?disabled=${this._loginLinkSent}
                  @input=${(e: any) => { this._loginEmail = e.target.value; }}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._sendSignInLink(); }}>
              </div>
              <button class="btn primary wide" ?disabled=${this._loginBusy || this._loginLinkSent} @click=${() => this._sendSignInLink()}>
                ${this._loginLinkSent ? '✓ Link Sent' : '📧 Send Sign-In Link'}
              </button>
              ${this._loginLinkSent ? html`<p class="muted" style="margin-top:0.6rem;text-align:center">✓ Sign-in link sent! Check your email and click the link to sign in.</p>` : nothing}
            </div>
          </div>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-root': AppRoot;
  }
}
